import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  Calendar as CalendarIcon, Star, ChevronLeft, ChevronRight, ChevronDown,
  CheckCircle, AlertCircle, AlertTriangle, Info, ArrowRight, UserPlus, Send,
  Wallet, DollarSign, MessageCircle, Mail, Smartphone, Eye, Clock, Users,
  TrendingUp, X, Download, Bell, Zap, Circle, FileText, BarChart3, Target,
  Check, Activity, Briefcase, Trash2, Phone
} from 'lucide-react';
import KpiRing from './KpiRing';
import KPIIntegrityIndicator from './KPIIntegrityIndicator';
import { theme } from '../../data/colors';
import { adminAuthHeader } from '../../data/api';

const API = process.env.REACT_APP_BACKEND_URL;

/* ═══════════════════════════════════════════════════════════════════════════
   DESIGN TOKENS — exact Figma spec values
   ═══════════════════════════════════════════════════════════════════════════ */
const C = {
  goldPrimary: '#D4AF37', goldDark: '#B8860B', goldMid: '#C5960C',
  goldDeep: '#8B6914', goldLight: '#F5D061',
  textPrimary: '#1a1a1a', textSecondary: '#444444', textMuted: '#5a5a5a',
  statusRed: '#DC2626', statusRedDark: '#B91C1C',
  statusGreen: '#22C55E', statusGreenDark: '#16A34A',
  statusAmber: '#F59E0B', statusAmberDark: '#D97706',
  chartBlue: '#2563EB', chartPurple: '#8B5CF6', chartPink: '#EC4899',
};

const FONT = {
  playfair: "'Playfair Display', serif",
  cormorant: "'Cormorant Garamond', serif",
  system: "system-ui, -apple-system, sans-serif",
};

const SHADOW = {
  glassStd: 'inset 2.5px 0 0 rgba(255,255,255,0.5), inset -2.5px 0 0 rgba(255,255,255,0.5), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(255,255,255,0.2), 0 16px 48px rgba(0,0,0,0.10), 0 6px 16px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)',
  glassGold: 'inset 2.5px 0 0 rgba(212,175,55,0.25), inset -2.5px 0 0 rgba(212,175,55,0.25), inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(255,255,255,0.2), 0 16px 48px rgba(0,0,0,0.10), 0 6px 16px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.03)',
  neuOuter: '6px 6px 14px rgba(0,0,0,0.1), -6px -6px 14px rgba(255,255,255,0.9), inset 0 0 0 1px rgba(255,255,255,0.5)',
  neuInner: 'inset 3px 3px 8px rgba(0,0,0,0.07), inset -2px -2px 6px rgba(255,255,255,0.8)',
  tooltip: '0 12px 32px rgba(0,0,0,0.1)',
  dropdown: '0 16px 40px rgba(0,0,0,0.14), 0 0 0 1px rgba(255,255,255,0.5)',
};

const GRAD = {
  goldBar: 'linear-gradient(90deg, #B8860B, #D4AF37, #F5D061, #D4AF37)',
  goldTrack: 'linear-gradient(90deg, #D4AF37 0%, #F5D061 60%, #D4AF37 100%)',
  goldDivider: 'linear-gradient(90deg, #D4AF37 0%, #F5D061 30%, rgba(212,175,55,0.3) 60%, transparent 100%)',
  topShine: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)',
};

const glassStd = {
  background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
  borderRadius: 16, boxShadow: SHADOW.glassStd, position: 'relative', minWidth: 0,
};
const glassGold = {
  background: 'rgba(255,255,255,0.18)', backdropFilter: 'blur(28px)', WebkitBackdropFilter: 'blur(28px)',
  borderRadius: 16, boxShadow: SHADOW.glassGold, position: 'relative', minWidth: 0,
};

function TopShine() {
  return <div style={{ position: 'absolute', top: 0, left: 16, right: 16, height: 1, background: GRAD.topShine, pointerEvents: 'none' }} />;
}

function Icon3D({ Icon, size = 46, radius = 14, accent = C.goldPrimary, accentEnd = C.goldDark }) {
  return (
    <div style={{ width: size, height: size, borderRadius: radius, background: `linear-gradient(145deg, ${accent}, ${accentEnd})`, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', boxShadow: `0 5px 17px ${accent}40, inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -1px 0 rgba(0,0,0,0.12)`, flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', borderRadius: `${radius}px ${radius}px 0 0`, background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)', pointerEvents: 'none' }} />
      <Icon size={size * 0.43} color="#fff" strokeWidth={2} />
    </div>
  );
}

function SharpBadge({ text, bg, color, border }) {
  return (
    <span style={{ display: 'inline-block', padding: '3px 8px', borderRadius: 0, background: bg, color, fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', fontFamily: FONT.system, border: border || 'none', lineHeight: '12px' }}>{text}</span>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   TIMELINE DATA (preserved exactly)
   ═══════════════════════════════════════════════════════════════════════════ */
const TIMELINE_MILESTONE_DEFS = [
  { id: 'm1', label: 'Jan', title: 'Save the Dates', task: 'Design & send save-the-date cards', module: 'Messaging', deadline: '2026-01-31' },
  { id: 'm2', label: 'Feb', title: 'Invitations Out', task: 'Mail formal invitations with RSVP card', module: 'Guests', deadline: '2026-02-28' },
  { id: 'm3', label: 'Mar', title: 'RSVP Deadline', task: 'All RSVPs due — finalize headcount', module: 'Guests', deadline: '2026-03-15' },
  { id: 'm4', label: 'Apr', title: 'Vendor Contracts', task: 'All vendor contracts signed & deposits paid', module: 'Vendors', deadline: '2026-04-30' },
  { id: 'm5', label: 'May', title: 'Catering Count', task: 'Submit final headcount to caterer', module: 'Vendors', deadline: '2026-05-15' },
  { id: 'm6', label: 'Jun', title: 'Final Payments', task: 'All vendor final payments due', module: 'Finance', deadline: '2026-06-30' },
  { id: 'm7', label: 'Jul', title: '80 Years of Connection', task: 'Event Day — day-of logistics & execution', module: 'All', deadline: '2026-07-12' },
];
function computeMilestoneStatus(d) { const diff = Math.ceil((new Date(d).getTime() - Date.now()) / 86400000); if (diff < 0) return 'completed'; if (diff <= 30) return 'active'; return 'upcoming'; }
export const TIMELINE_MILESTONES = TIMELINE_MILESTONE_DEFS.map(m => ({ ...m, status: computeMilestoneStatus(m.deadline), deadline: new Date(m.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }));

/* ═══════════════════════════════════════════════════════════════════════════
   HELPERS (preserved exactly)
   ═══════════════════════════════════════════════════════════════════════════ */
function daysUntilEvent(dl) { if (!dl) return 0;
  // Bare `new Date("YYYY-MM-DD")` parses as UTC midnight = the previous EVENING locally, so the
  // tile dropped a day after ~8 PM ET and disagreed with the header. Parse ISO dates as LOCAL
  // midnight (same rule as eventDateStr below and AdminPage's header badge).
  const s = String(dl);
  const t = (/^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(s + 'T00:00:00') : new Date(s)).getTime();
  return Math.max(0, Math.ceil((t - Date.now()) / 86400000)); }
function safePct(n, d) { if (d === 0) return 0; return Math.round(Math.min((n / d) * 100, 100)); }
const fmt = (n) => `$${(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
function fmtTime(iso) { if (!iso) return ''; const d = new Date(iso); if (isNaN(d)) return ''; return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); }

/* ═══════════════════════════════════════════════════════════════════════════
   ROW 1 — KPI COMMAND BAR
   ═══════════════════════════════════════════════════════════════════════════ */
function CommandBar({ guestStats, unreadCount, eventConfig, budget, vendors, onNavigate }) {
  const daysLeft = daysUntilEvent(eventConfig.date);
  const paymentsDue = (vendors ?? []).filter(v => !!v.next_due_at).length;
  let alertCount = paymentsDue;
  if (guestStats.failed > 0) alertCount += 1;
  if (unreadCount > 0) alertCount += 1;
  if (guestStats.total > 0 && guestStats.pending > guestStats.total * 0.4) alertCount += 1;
  const budgetSpent = budget ? fmt(budget.spent_budget) : '—';
  const eventDateStr = (() => { if (!eventConfig.date) return '—'; const s = eventConfig.date; const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/); if (m) { const d = new Date(`${s}T00:00:00`); return isNaN(d) ? s : d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); } const d2 = new Date(s); return isNaN(d2) ? s : d2.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }); })();
  // Parse the date string as LOCAL-midnight (mirrors eventDateStr above). Do NOT do `new Date(eventConfig.date)`
  // for bare 'YYYY-MM-DD' strings — JS parses those as UTC midnight, which is the previous day in any negative
  // UTC offset. That bug shipped "Friday" for 2026-07-25 (which is Saturday). Protected by `test_dashboard_kpi_integrity`.
  const dayName = (() => {
    if (!eventConfig.date) return '';
    const s = String(eventConfig.date);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    const d = m ? new Date(`${s}T00:00:00`) : new Date(s);
    return isNaN(d) ? '' : d.toLocaleDateString('en-US', { weekday: 'long' });
  })();

  // Plate cost computation — drives the "Plate Cost Per Guest" tile.
  //  PROTECTED KPI CONSUMER — sourced from `guestStats.{adults,children}_attending`,
  //  which flows from /api/dashboard/stats → `_compute_guest_kpi_snapshot()`.
  //
  //  HARD RULE: `unknown_age_attending` is NOT included in plate cost. When it's > 0,
  //  it is an integrity exception (invariant I2b broken). The integrity banner
  //  surfaces the offending guests and admin must resolve (assign ADULT/CHILD or
  //  attach to a logistics-bearing party) before plate cost is accurate.
  const adultsYes = guestStats.adults_yes ?? guestStats.adults_attending ?? 0;
  const childrenYes = guestStats.children_yes ?? guestStats.children_attending ?? 0;
  const unknownAgeYes = guestStats.unknown_age_attending ?? 0;
  // ─── Plate-cost source resolution (Feb 2026) ────────────────────────────
  // Source-of-truth precedence:
  //   1. First Venue vendor (role_category === 'Venue', earliest created_at)
  //      pricing_conditions.adult_rate / child_rate.
  //   2. Fallback: event_config.adult_plate_cost / child_plate_cost
  //      (legacy field, still readable for backwards compat).
  //   3. Empty state: neither configured → existing "Set plate costs…" prompt.
  // Multi-venue future: deferred — single venue today; pick first by
  // created_at and leave architecture untouched.
  const venueVendor = (() => {
    const venues = (vendors || []).filter(v => (v.role_category || '').toLowerCase() === 'venue');
    if (venues.length === 0) return null;
    const sorted = [...venues].sort((a, b) =>
      (a.created_at || '').localeCompare(b.created_at || '')
    );
    return sorted[0];
  })();
  const venuePc = venueVendor?.pricing_conditions || {};
  const venueAdultRate = (venuePc.adult_rate != null) ? Number(venuePc.adult_rate) : null;
  const venueChildRate = (venuePc.child_rate != null) ? Number(venuePc.child_rate) : null;
  const cfgAdultPlate = (eventConfig.adult_plate_cost != null && eventConfig.adult_plate_cost !== '')
    ? Number(eventConfig.adult_plate_cost) : null;
  const cfgChildPlate = (eventConfig.child_plate_cost != null && eventConfig.child_plate_cost !== '')
    ? Number(eventConfig.child_plate_cost) : null;

  // ─── Threshold evaluation (May 2026) ────────────────────────────────────
  // The vendor's pricing_conditions support conditional rates of the shape
  // "Child = $X if {count_field} {operator} {threshold}". Before May 2026
  // the dashboard ignored the threshold and applied venue rates uncondit-
  // ionally, which over-discounted plate cost. Now we evaluate the
  // condition against the live counts and only apply the conditional rate
  // when the condition is met.
  //
  // Fallback rule when a threshold IS configured but NOT yet met:
  //   • Child → priced at the ACTIVE ADULT rate (this matches the common
  //     venue convention surfaced in the wizard: before the volume target
  //     hits, children pay the same as adults; the discount kicks in only
  //     once the headcount commitment is real).
  //   • Adult → falls back to the legacy event_config.adult_plate_cost when
  //     it exists, else 0.
  // When NO threshold is configured (rate set with no operator/threshold),
  // the rate applies normally — no behavior change for those venues.
  const headCounts = {
    adult_count:     Number(adultsYes) || 0,
    child_count:     Number(childrenYes) || 0,
    total_headcount: (Number(adultsYes) || 0) + (Number(childrenYes) || 0),
  };
  const evalCondition = (value, op, threshold) => {
    const v = Number(value);
    const t = Number(threshold);
    if (Number.isNaN(v) || Number.isNaN(t) || !op) return false;
    switch (op) {
      case '>=': return v >= t;
      case '>':  return v > t;
      case '<=': return v <= t;
      case '<':  return v < t;
      case '=':
      case '==': return v === t;
      case '!=': return v !== t;
      default:   return false;
    }
  };

  // Adult active rate — supports an optional adult-side threshold for
  // future-proofing, but never affects the child-side fallback.
  const adultThresholdConfigured = (
    venueAdultRate != null &&
    venuePc.adult_threshold != null &&
    !!venuePc.adult_operator
  );
  const adultThresholdMet = adultThresholdConfigured
    ? evalCondition(
        headCounts[venuePc.adult_count_field || 'adult_count'],
        venuePc.adult_operator,
        venuePc.adult_threshold,
      )
    : true;
  let activeAdultRate;
  if (venueAdultRate != null) {
    activeAdultRate = adultThresholdMet ? venueAdultRate : (cfgAdultPlate || 0);
  } else {
    activeAdultRate = cfgAdultPlate || 0;
  }

  // Child active rate — when threshold is configured but not met, child is
  // priced at the active adult rate (per venue convention). When met OR
  // when no threshold is configured, the configured child rate applies
  // normally.
  const childThresholdConfigured = (
    venueChildRate != null &&
    venuePc.child_threshold != null &&
    !!venuePc.child_operator
  );
  const childCountFieldKey = venuePc.child_count_field || 'adult_count';
  const childThresholdMet = childThresholdConfigured
    ? evalCondition(headCounts[childCountFieldKey], venuePc.child_operator, venuePc.child_threshold)
    : true;
  let activeChildRate;
  if (childThresholdConfigured) {
    activeChildRate = childThresholdMet ? venueChildRate : activeAdultRate;
  } else if (venueChildRate != null) {
    activeChildRate = venueChildRate;
  } else if (cfgChildPlate != null) {
    activeChildRate = cfgChildPlate;
  } else {
    activeChildRate = activeAdultRate;
  }

  // For the legacy per-rate variables used elsewhere on this tile.
  const adultPlate = activeAdultRate;
  const childPlate = activeChildRate;
  const plateSource = (venueAdultRate != null || venueChildRate != null)
    ? 'venue'
    : ((cfgAdultPlate != null || cfgChildPlate != null) ? 'event_config' : 'none');
  const adultTotal = adultsYes * adultPlate;
  const childTotal = childrenYes * childPlate;
  // ── Vendor-meal cost: a SEPARATE category, governed by event-level settings.
  //    Rule: total = (Σ meal_count where needs_meal) × eventConfig.vendor_meal_rate, ONLY
  //    when eventConfig.vendor_meals_enabled is true. No per-vendor rate; NO adult/child
  //    fallback. Enabled-but-no-rate → warning + excluded. Disabled → counted, not charged.
  //    Never touches the adult/child RSVP counts or formula above.
  const vendorMeals = (vendors || []).reduce((s, v) => s + (v.needs_meal ? (Number(v.meal_count) || 0) : 0), 0);
  const vendorMealsEnabled = eventConfig.vendor_meals_enabled === true;
  const vendorMealRate = Number(eventConfig.vendor_meal_rate) > 0 ? Number(eventConfig.vendor_meal_rate) : null;
  const vendorMealsIncluded = vendorMealsEnabled && vendorMeals > 0 && vendorMealRate != null;
  const vendorMealRateMissing = vendorMealsEnabled && vendorMeals > 0 && vendorMealRate == null; // enabled but no rate
  const vendorMealsNotCharged = !vendorMealsEnabled && vendorMeals > 0;                          // present but disabled
  const vendorMealTotal = vendorMealsIncluded ? vendorMeals * vendorMealRate : 0;

  // Projected note metadata — only surfaced when the child threshold is
  // configured and not yet met, so the host sees "what plate cost will be
  // after threshold" without us silently applying it as the active total.
  const showChildThresholdNote = childThresholdConfigured && !childThresholdMet;
  let childThresholdNote = null;
  if (showChildThresholdNote) {
    const projectedChildTotal = childrenYes * Number(venueChildRate);
    const opLabel = ({ '>=': '≥', '<=': '≤', '=': '=', '==': '=', '!=': '≠' })[venuePc.child_operator] || venuePc.child_operator;
    const fieldLabel = (
      childCountFieldKey === 'adult_count'     ? 'adults' :
      childCountFieldKey === 'child_count'     ? 'children' :
      childCountFieldKey === 'total_headcount' ? 'total guests' :
      childCountFieldKey
    );
    const threshN = Number(venuePc.child_threshold);
    childThresholdNote = `Child discount activates at ${opLabel} ${threshN} ${fieldLabel}: projected child cost would be ${fmt(projectedChildTotal)} after threshold.`;
  }

  const handleAlertsClick = () => {
    try {
      const el = document.getElementById('priority-alerts-anchor');
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); return; }
    } catch { /* noop */ }
    onNavigate?.('dashboard');
  };

  return (
    <div data-testid="command-bar" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
      {/* Event Date */}
      <div style={{ ...glassGold, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Icon3D Icon={CalendarIcon} accent={C.goldPrimary} accentEnd={C.goldDark} />
          <div>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>Event Date</span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 22, color: C.textPrimary, lineHeight: 1.15, display: 'block' }}>{eventDateStr}</span>
            <span style={{ fontFamily: FONT.system, fontSize: 10, fontWeight: 500, color: C.textSecondary, marginTop: 2, display: 'block' }}>{dayName}</span>
          </div>
        </div>
      </div>
      {/* Days Left */}
      <div style={{ ...glassGold, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Icon3D Icon={Clock} accent={C.goldMid} accentEnd={C.goldDeep} />
          <div>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>Days Left</span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 22, color: C.textPrimary, lineHeight: 1.15, display: 'block' }}>{daysLeft}</span>
            <span style={{ fontFamily: FONT.system, fontSize: 10, fontWeight: 500, color: C.textSecondary, marginTop: 2, display: 'block' }}>until the big day</span>
          </div>
        </div>
      </div>
      {/* Total Running Plate Cost — replaces the duplicate "Guests Confirmed" tile */}
      <div data-testid="plate-cost-tile" style={{ ...glassGold, padding: 16 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Icon3D Icon={DollarSign} accent={C.statusGreen} accentEnd={C.statusGreenDark} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textSecondary, display: 'block' }}>Total Running Plate Cost</span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 20, color: C.textPrimary, lineHeight: 1.15, display: 'block' }}>
              {fmt(adultTotal + childTotal + vendorMealTotal)}
            </span>
          </div>
          {vendorMeals > 0 && (
            <div
              data-testid="plate-cost-vendor-chip"
              title={vendorMealsIncluded ? 'Vendor meals included in cost' : 'Vendor meals not included in cost'}
              style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 9px', borderRadius: 999, flexShrink: 0,
                background: vendorMealsIncluded ? 'rgba(74,140,90,0.12)' : 'rgba(120,120,120,0.10)',
                color: vendorMealsIncluded ? C.statusGreen : C.textSecondary, fontSize: 11, fontWeight: 700 }}
            >
              <Briefcase size={12} /> {vendorMeals}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, fontFamily: FONT.system, fontSize: 10, lineHeight: 1.35 }}>
          <div data-testid="plate-cost-adult" style={{ color: C.textPrimary }}>
            <span style={{ fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 9, marginRight: 4 }}>Adults</span>
            {adultsYes} × {fmt(adultPlate)} = <span style={{ fontWeight: 700 }}>{fmt(adultTotal)}</span>
          </div>
          <div data-testid="plate-cost-child" style={{ color: C.textPrimary }}>
            <span style={{ fontWeight: 700, color: C.chartBlue, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 9, marginRight: 4 }}>Children</span>
            {childrenYes} × {fmt(childPlate)} = <span style={{ fontWeight: 700 }}>{fmt(childTotal)}</span>
          </div>
          {vendorMealsIncluded && (
            <div data-testid="plate-cost-vendor-meals" style={{ color: C.textPrimary }}>
              <span style={{ fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 9, marginRight: 4 }}>Vendor meals</span>
              {vendorMeals} × {fmt(vendorMealRate)} = <span style={{ fontWeight: 700 }}>{fmt(vendorMealTotal)}</span>
              <span style={{ color: C.statusGreen, marginLeft: 5, fontWeight: 600 }}>· included</span>
            </div>
          )}
          {vendorMealRateMissing && (
            <div data-testid="plate-cost-vendor-meals-warning" style={{ color: '#B45309', display: 'flex', alignItems: 'flex-start', gap: 4, fontWeight: 600 }}>
              <span style={{ flexShrink: 0 }}>⚠</span>
              <span>Vendor meals enabled but no rate set — {vendorMeals} vendor meal{vendorMeals !== 1 ? 's' : ''} <b>excluded</b>. Set the rate in Settings → Event Identity.</span>
            </div>
          )}
          {vendorMealsNotCharged && (
            <div data-testid="plate-cost-vendor-meals-disabled" style={{ color: C.textSecondary }}>
              <span style={{ fontWeight: 700, color: C.goldDark, textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: 9, marginRight: 4 }}>Vendor meals</span>
              {vendorMeals} meal{vendorMeals !== 1 ? 's' : ''} · <b>not charged</b> (enable in Settings)
            </div>
          )}
        </div>
        {(!adultPlate && !childPlate) && (
          <div data-testid="plate-cost-empty-state" style={{ fontSize: 9, color: C.textSecondary, marginTop: 6, fontStyle: 'italic' }}>
            Set plate costs on the Venue vendor (Vendors → Venue → Pricing).
          </div>
        )}
        {(adultPlate || childPlate) && (
          <div data-testid={`plate-cost-source-${plateSource}`} style={{ fontSize: 9, color: C.textMuted, marginTop: 6, fontStyle: 'italic' }}>
            {plateSource === 'venue'
              ? `Source: Venue vendor — ${venueVendor.company_name}`
              : 'Source: Settings → Event Identity (legacy)'}
          </div>
        )}
        {showChildThresholdNote && (
          <div
            data-testid="plate-cost-child-threshold-note"
            style={{
              marginTop: 6,
              padding: '4px 8px',
              borderRadius: 6,
              background: 'rgba(212,175,55,0.06)',
              border: '1px solid rgba(212,175,55,0.20)',
              fontFamily: FONT.cormorant,
              fontSize: 10.5,
              fontStyle: 'italic',
              color: C.textSecondary,
              lineHeight: 1.35,
            }}
          >
            {childThresholdNote}
          </div>
        )}
      </div>
      {/* Budget Used */}
      <div style={{ ...glassGold, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Icon3D Icon={DollarSign} accent={C.goldPrimary} accentEnd={C.goldDark} />
          <div>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>Budget Used</span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 22, color: C.textPrimary, lineHeight: 1.15, display: 'block' }}>{budgetSpent}</span>
            {budget && <span style={{ fontFamily: FONT.system, fontSize: 10, fontWeight: 500, color: C.textSecondary, marginTop: 2, display: 'block' }}>of {fmt(budget.total_budget)} total</span>}
          </div>
        </div>
      </div>
      {/* Active Alerts — routes to the on-page Priority Alerts panel, not messaging */}
      <div data-testid="active-alerts-tile" style={{ ...glassGold, padding: 20, cursor: 'pointer' }} onClick={handleAlertsClick}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Icon3D Icon={Bell} accent={C.statusRed} accentEnd={C.statusRedDark} />
          <div>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.1em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>Active Alerts</span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 22, color: C.textPrimary, lineHeight: 1.15, display: 'block' }}>{alertCount}</span>
            <span style={{ fontFamily: FONT.system, fontSize: 10, fontWeight: 500, color: C.textSecondary, marginTop: 2, display: 'block' }}>{alertCount > 0 ? 'require attention' : 'all clear'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROW 2 — ATTENDANCE + DEMOGRAPHICS
   ═══════════════════════════════════════════════════════════════════════════ */

/**
 * DriftDot — operator-only KPI drift indicator (Feb 2026).
 *
 * Renders a 4×4 px muted-amber dot in the top-right corner of the RSVP-Yes
 * ring tile ONLY when `drift_check.has_drift === true`. Click reveals a
 * compact popover listing the offending parties.
 *
 * - Hidden entirely when drift_check.has_drift is false (default).
 * - Admin-only — never imported by any guest-facing module.
 * - Read-only — never mutates any data; the popover is informational and
 *   contains no action buttons. Operator hint mentions the surgical
 *   patch-rsvp-logistics endpoint as the remediation path, but does not
 *   call it.
 */
function DriftDot({ driftCheck }) {
  const [open, setOpen] = useState(false);
  // Dismiss on outside click (hook MUST be called unconditionally — runs no-op
  // when the dot is hidden or the popover is closed).
  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = () => setOpen(false);
    const t = setTimeout(() => document.addEventListener('click', onDocClick, { once: true }), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', onDocClick); };
  }, [open]);

  if (!driftCheck || driftCheck.has_drift !== true) return null;
  const offenders = Array.isArray(driftCheck.offenders) ? driftCheck.offenders : [];
  const visibleOffenders = offenders.slice(0, 5);
  const hiddenCount = Math.max(0, offenders.length - visibleOffenders.length);

  return (
    <div
      data-testid="drift-dot-anchor"
      style={{
        position: 'absolute', top: 6, right: 6, zIndex: 4,
      }}
      onClick={(e) => { e.stopPropagation(); setOpen(v => !v); }}
    >
      <span
        data-testid="drift-dot"
        title="KPI drift detected — click for details"
        aria-label="KPI drift detected — click for details"
        style={{
          display: 'block',
          width: 6, height: 6, borderRadius: '50%',
          background: '#D9A33C',
          boxShadow: '0 0 0 2px rgba(217,163,60,0.18)',
          cursor: 'pointer',
        }}
      />
      {open && (
        <div
          data-testid="drift-popover"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute',
            top: 14, right: -4,
            width: 280,
            background: 'linear-gradient(180deg, #FAF7F0 0%, #F2EBDD 100%)',
            border: '1px solid rgba(217,163,60,0.45)',
            borderRadius: 8,
            boxShadow: '0 12px 28px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.10)',
            padding: '12px 14px',
            fontFamily: "'Cormorant Garamond', serif",
            color: '#2A201C',
          }}
        >
          <div style={{
            fontSize: 12, fontWeight: 700, color: '#8F7432',
            textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6,
          }}>
            KPI Drift Detected
          </div>
          <div style={{
            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
            fontSize: 11, lineHeight: 1.5, color: '#3A2F2A', marginBottom: 8,
          }}>
            Σ logistics: <b>{driftCheck.logistics_total}</b>{' · '}
            Σ per-guest YES: <b>{driftCheck.per_guest_yes_total}</b>
            {driftCheck.legit_plus_ones > 0 && (
              <>{' · '}plus-ones: <b>{driftCheck.legit_plus_ones}</b></>
            )}
            {' · '}Δ <b>{driftCheck.net_delta >= 0 ? '+' : ''}{driftCheck.net_delta}</b>
          </div>
          {visibleOffenders.length > 0 && (
            <ul style={{
              listStyle: 'none', padding: 0, margin: '0 0 8px',
              fontSize: 12, lineHeight: 1.45,
            }}>
              {visibleOffenders.map(o => (
                <li key={o.party_id} style={{ marginBottom: 4 }}>
                  <span style={{ fontWeight: 600 }}>{o.party_name}</span>
                  {' — logistics '}{o.log_total}{' vs members '}{o.yes_member_total}
                  {o.additional_names_count > 0 && (
                    <span style={{ color: '#8F7432' }}> + {o.additional_names_count} unpromoted</span>
                  )}
                </li>
              ))}
              {hiddenCount > 0 && (
                <li style={{ fontStyle: 'italic', color: '#7C6A5A' }}>
                  + {hiddenCount} more party{hiddenCount === 1 ? '' : 'ies'}
                </li>
              )}
            </ul>
          )}
          <div style={{
            fontSize: 10, fontStyle: 'italic', color: '#8F7432',
            borderTop: '1px solid rgba(217,163,60,0.25)',
            paddingTop: 6, marginTop: 4, lineHeight: 1.4,
          }}>
            Remediate via <span style={{ fontFamily: 'ui-monospace, monospace' }}>
              POST /api/parties/&#123;id&#125;/patch-rsvp-logistics
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function AttendanceRow({ guestStats, minimumGuestTarget, onNavigate }) {
  const attendanceRate = safePct(guestStats.rsvp_yes ?? 0, guestStats.total);
  const rsvpCompletion = safePct((guestStats.rsvp_yes ?? 0) + (guestStats.rsvp_no ?? 0), guestStats.total);

  // May 2026: ring-level KPI drilldown (replaces the prior adult/child sub-click
  // technical breakdown — superseded by the host-facing party-grouped modal).
  // - RSVP Yes / RSVP No → polished party-grouped modal (this row's RsvpListModal).
  // - Total Invited / Pending RSVP → route to Guests tab with the matching filter.
  const [rsvpListOpen, setRsvpListOpen] = useState(null); // { status, parties, error } | null
  const [rsvpListLoading, setRsvpListLoading] = useState(false);

  const navToGuestsWithRsvp = useCallback((rsvp) => {
    try { sessionStorage.setItem('admin_guest_list_rsvp_filter', String(rsvp || 'all')); } catch (_) {}
    if (typeof onNavigate === 'function') onNavigate('guests');
  }, [onNavigate]);

  const openRsvpList = useCallback(async (status, scope = 'all') => {
    setRsvpListLoading(true);
    setRsvpListOpen({ status, scope, parties: [], error: null });
    try {
      const [guests, parties] = await Promise.all([
        fetch(`${API}/api/guests`).then(r => r.json()),
        fetch(`${API}/api/parties`).then(r => r.json()),
      ]);
      const wantedStatus = status.toUpperCase();
      const partyById = {};
      (Array.isArray(parties) ? parties : []).forEach((p) => {
        if (p && p.id) partyById[p.id] = p;
      });
      const groups = {};
      (Array.isArray(guests) ? guests : []).forEach((g) => {
        const rs = (g.rsvp_status || '').toUpperCase();
        if (rs !== wantedStatus) return;
        const pid = g.party_id || g.id || `solo:${g.id}`;
        if (!groups[pid]) {
          groups[pid] = {
            party_id: pid,
            party_name: g.party_name || (g.first_name ? `${g.first_name}'s party` : 'Party'),
            guests: [],
            adults: 0,
            children: 0,
          };
        }
        const fullName = [g.first_name, g.last_name].filter(Boolean).join(' ').trim() || 'Guest';
        const ag = (g.age_group || '').toUpperCase();
        const isChild = ag === 'CHILD';
        groups[pid].guests.push({ name: fullName, isChild });
        if (ag === 'ADULT') groups[pid].adults += 1;
        else if (ag === 'CHILD') groups[pid].children += 1;
      });
      const out = Object.values(groups).map((grp) => {
        const partyDoc = partyById[grp.party_id];
        // Prefer per-guest age data; fall back to party-level attending counts
        // (counts-only payload — no PII) for parties that submitted at the
        // party level without per-guest age tagging.
        const attendingCounts = partyDoc?.rsvp_attending_counts || null;
        let adults = grp.adults;
        let children = grp.children;
        let derivedFromParty = false;
        if (status === 'yes' && (adults + children === 0) && attendingCounts) {
          adults = Number(attendingCounts.adults_count || 0);
          children = Number(attendingCounts.children_count || 0);
          derivedFromParty = true;
        }
        let summary;
        if (status === 'no') {
          summary = grp.guests.length === 1 ? '1 guest' : `${grp.guests.length} guests`;
        } else if (adults + children === 0) {
          summary = 'Submitted at party level';
        } else if (children === 0) {
          summary = `${adults} ${adults === 1 ? 'adult' : 'adults'}`;
        } else if (adults === 0) {
          summary = `${children} ${children === 1 ? 'child' : 'children'}`;
        } else {
          summary = `${adults} ${adults === 1 ? 'adult' : 'adults'} · ${children} ${children === 1 ? 'child' : 'children'}`;
        }
        return {
          ...grp,
          summary,
          submittedAtPartyLevel: derivedFromParty,
          // Children present (either via per-guest age data OR party-level logistics).
          hasChildren: children > 0,
          childrenCount: children,
        };
      });
      // Children scope: only parties that include at least one child.
      const filtered = scope === 'children' ? out.filter(p => p.hasChildren) : out;
      filtered.sort((a, b) =>
        (b.guests.length - a.guests.length) ||
        a.party_name.localeCompare(b.party_name)
      );
      setRsvpListOpen({ status, scope, parties: filtered, error: null });
    } catch (_) {
      setRsvpListOpen({ status, scope, parties: [], error: "Couldn't load the list. Please try again." });
    } finally {
      setRsvpListLoading(false);
    }
  }, []);

  const rings = [
    { label: 'Total Invited', value: guestStats.total, total: guestStats.total, displayValue: String(guestStats.total), color: 'gold' },
    { label: 'RSVP Yes', value: guestStats.rsvp_yes ?? 0, total: guestStats.total, displayValue: String(guestStats.rsvp_yes ?? 0), color: 'success', adults: guestStats.adults_yes ?? 0, children: guestStats.children_yes ?? 0, showBreakdown: true,
      // Child sub-row click → drilldown filtered to parties that include children,
      // with each child guest tagged inline. stopPropagation prevents the outer
      // ring wrapper from also firing the 'all'-scope modal.
      onChildClick: (e) => { e?.stopPropagation?.(); openRsvpList('yes', 'children'); },
    },
    { label: 'RSVP No', value: guestStats.rsvp_no ?? 0, total: guestStats.total, displayValue: String(guestStats.rsvp_no ?? 0), color: 'danger' },
    { label: 'Pending RSVP', value: guestStats.pending, total: guestStats.total, displayValue: String(guestStats.pending), color: 'warning' },
  ];
  // Minimum Target progress tile — replaces the static number with a gauge + remaining/percentage.
  const rsvpYes = guestStats.rsvp_yes ?? 0;
  const target = Number(minimumGuestTarget) || 0;
  const targetPct = target > 0 ? Math.min(100, Math.round((rsvpYes / target) * 100)) : 0;
  const targetRemaining = Math.max(0, target - rsvpYes);
  const targetColor = target === 0 ? C.textSecondary : (targetPct >= 100 ? C.statusGreen : targetPct >= 66 ? C.goldPrimary : C.statusAmber);

  const stats = [
    { kind: 'target', label: 'Minimum Target', target, rsvpYes, targetPct, targetRemaining, targetColor, Icon: Target },
    { kind: 'plain', label: 'Attendance Rate', value: `${attendanceRate}%`, Icon: TrendingUp, accent: C.statusGreen, accentEnd: C.statusGreenDark },
    { kind: 'plain', label: 'Invitations Sent', value: String(guestStats.official_invitations_sent ?? 0), Icon: Send, accent: C.chartBlue, accentEnd: '#1D4ED8' },
    { kind: 'plain', label: 'RSVP Completion', value: `${rsvpCompletion}%`, Icon: Check, accent: C.chartPurple, accentEnd: '#7C3AED' },
  ];

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '7fr 5fr', gap: 16, alignItems: 'stretch' }}>
      {/* Gauges — all 4 on single row, premium depth */}
      <div style={{ ...glassGold, padding: '20px 20px', display: 'flex', flexDirection: 'column' }}>
        <TopShine />
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block', marginBottom: 16 }}>Attendance Overview</span>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 16, flex: 1 }}>
          {rings.map((ring, idx) => {
            const isRsvpYes = idx === 1;
            // Map ring index → drilldown action (May 2026).
            // 0=Total Invited, 1=RSVP Yes, 2=RSVP No, 3=Pending RSVP.
            const onRingClick =
              idx === 0 ? () => navToGuestsWithRsvp('all')      :
              idx === 1 ? () => openRsvpList('yes')              :
              idx === 2 ? () => openRsvpList('no')               :
              idx === 3 ? () => navToGuestsWithRsvp('pending')   : null;
            const ringTestId =
              idx === 0 ? 'attendance-ring-total-invited' :
              idx === 1 ? 'attendance-ring-rsvp-yes' :
              idx === 2 ? 'attendance-ring-rsvp-no' :
              idx === 3 ? 'attendance-ring-pending' : `attendance-ring-${idx}`;
            return (
              <div key={ring.label} style={{
                position: 'relative',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                flex: isRsvpYes ? '0 0 auto' : 1,
              }}>
                {isRsvpYes && (
                  <DriftDot driftCheck={guestStats.drift_check} />
                )}
                <div
                  data-testid={ringTestId}
                  role="button"
                  tabIndex={0}
                  onClick={onRingClick || undefined}
                  onKeyDown={(e) => {
                    if (!onRingClick) return;
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRingClick(); }
                  }}
                  style={{
                    borderRadius: '50%', padding: isRsvpYes ? 6 : 5,
                    background: 'linear-gradient(145deg, rgba(255,255,255,0.9), rgba(230,225,215,0.6))',
                    boxShadow: `8px 8px 18px rgba(0,0,0,0.1), -8px -8px 18px rgba(255,255,255,0.95), inset 0 0 0 1px rgba(255,255,255,0.6)`,
                    cursor: onRingClick ? 'pointer' : 'default',
                    transition: 'transform 160ms cubic-bezier(0.22,1,0.36,1), box-shadow 160ms',
                    outline: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!onRingClick) return;
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow =
                      `10px 12px 24px rgba(0,0,0,0.14), -8px -8px 18px rgba(255,255,255,1), inset 0 0 0 1px rgba(212,175,55,0.45)`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow =
                      `8px 8px 18px rgba(0,0,0,0.1), -8px -8px 18px rgba(255,255,255,0.95), inset 0 0 0 1px rgba(255,255,255,0.6)`;
                  }}
                >
                  <div style={{
                    borderRadius: '50%',
                    boxShadow: 'inset 4px 4px 10px rgba(0,0,0,0.06), inset -3px -3px 8px rgba(255,255,255,0.85)',
                  }}>
                    <KpiRing {...ring} size={isRsvpYes ? 'lg' : 'md'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {/* Stat blocks — 2x2 grid, enlarged labels */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {stats.map(s => {
          if (s.kind === 'target') {
            return (
              <div key={s.label} data-testid="minimum-target-tile" style={{ ...glassStd, padding: '14px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <TopShine />
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Icon3D Icon={s.Icon} size={36} radius={10} accent={s.targetColor} accentEnd={s.targetColor} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary, display: 'block', marginBottom: 2 }}>{s.label}</span>
                    <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 20, color: C.textPrimary, lineHeight: 1.1 }}>
                      {s.target === 0 ? '—' : `${s.rsvpYes} / ${s.target}`}
                    </span>
                  </div>
                  {s.target > 0 && (
                    <div data-testid="minimum-target-pct" style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 15, color: s.targetColor }}>
                      {s.targetPct}%
                    </div>
                  )}
                </div>
                {s.target > 0 && (
                  <>
                    {/* Progress bar */}
                    <div style={{ position: 'relative', height: 8, background: 'rgba(0,0,0,0.06)', borderRadius: 4, overflow: 'hidden', boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)' }}>
                      <div data-testid="minimum-target-fill" style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: `${s.targetPct}%`, background: `linear-gradient(90deg, ${s.targetColor}, ${s.targetColor}CC)`, borderRadius: 4, boxShadow: `0 0 8px ${s.targetColor}66`, transition: 'width 0.4s ease-out' }} />
                    </div>
                    {/* Remaining caption */}
                    <div style={{ fontFamily: FONT.system, fontSize: 10, color: C.textSecondary, fontWeight: 500 }}>
                      {s.targetRemaining > 0
                        ? `${s.targetRemaining} more confirmed guest${s.targetRemaining === 1 ? '' : 's'} needed`
                        : 'Minimum target reached ✓'}
                    </div>
                  </>
                )}
                {s.target === 0 && (
                  <div style={{ fontFamily: FONT.system, fontSize: 10, color: C.textSecondary, fontStyle: 'italic' }}>
                    Set minimum target in Settings
                  </div>
                )}
              </div>
            );
          }
          return (
            <div key={s.label} style={{ ...glassStd, padding: '18px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <TopShine />
              <Icon3D Icon={s.Icon} size={42} radius={12} accent={s.accent} accentEnd={s.accentEnd} />
              <div style={{ flex: 1 }}>
                <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>{s.label}</span>
                <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 26, color: C.textPrimary, lineHeight: 1.1 }}>{s.value}</span>
              </div>
            </div>
          );
        })}
      </div>
      {/* KPI Drill-down Modal — May 2026, RSVP Yes/No rings */}
      {rsvpListOpen && (
        <RsvpListModal
          status={rsvpListOpen.status}
          scope={rsvpListOpen.scope || 'all'}
          loading={rsvpListLoading}
          parties={rsvpListOpen.parties}
          errorMsg={rsvpListOpen.error}
          onClose={() => setRsvpListOpen(null)}
          onViewInGuests={() => {
            const target = rsvpListOpen.status;
            setRsvpListOpen(null);
            navToGuestsWithRsvp(target);
          }}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   KPI Drill-down Modal — RSVP-Yes adult/child attribution viewer (Feb 2026)
   Truthful by design:
     • guest_age_group rows → exact person, exact age_group label
     • party_rsvp_logistics rows → party name + aggregate count + member list
       with explicit note that per-person age is NOT stored at this level.
   ═══════════════════════════════════════════════════════════════════════════ */
function KpiBreakdownModal({ bucket, loading, data, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const rows = (data?.[bucket] || []);
  const totalCount = data?.totals?.[bucket] ?? 0;
  const bucketLabel = bucket === 'adults' ? 'Adults' : 'Children';

  return (
    <div
      data-testid="kpi-breakdown-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        data-testid="kpi-breakdown-modal"
        style={{
          width: 640, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          background: 'rgba(255,255,255,0.98)', borderRadius: 16,
          boxShadow: '0 24px 80px rgba(0,0,0,0.18)', overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{ padding: '18px 24px 14px', borderBottom: '1px solid rgba(0,0,0,0.08)', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
              RSVP Yes — {bucketLabel} ({totalCount})
            </div>
            <div style={{ fontSize: 12, color: C.textMuted, marginTop: 4, lineHeight: 1.5 }}>
              Each row below shows the source path that contributed to the count. Aggregate party-level entries are clearly labeled.
            </div>
          </div>
          <button
            data-testid="kpi-breakdown-close"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: 22, color: C.textSecondary, cursor: 'pointer', lineHeight: 1, padding: 4 }}
          >×</button>
        </div>
        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 0' }}>
          {loading && (
            <div style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>Loading breakdown…</div>
          )}
          {!loading && rows.length === 0 && (
            <div data-testid="kpi-breakdown-empty" style={{ padding: 24, textAlign: 'center', color: C.textMuted, fontStyle: 'italic' }}>
              No one currently counted as {bucketLabel.toLowerCase()}.
            </div>
          )}
          {!loading && rows.map((row, idx) => {
            if (row.source === 'guest_age_group') {
              return (
                <div key={`g-${row.guest_id || idx}`} data-testid={`kpi-breakdown-row-guest-${row.guest_id}`} style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: C.textPrimary, fontSize: 14 }}>{row.name || '(unnamed)'}</div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>
                      Source: <code style={{ background: 'rgba(34,197,94,0.10)', color: '#15803D', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>guest age_group = {row.age_group}</code>
                      {row.party_name ? <span> · in <em>{row.party_name}</em></span> : null}
                    </div>
                  </div>
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, background: 'rgba(34,197,94,0.12)', color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                    Per-person
                  </span>
                </div>
              );
            }
            // party_rsvp_logistics — render with explicit aggregate framing.
            return (
              <div key={`p-${row.party_id}-${idx}`} data-testid={`kpi-breakdown-row-party-${row.party_id}`} style={{ padding: '14px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', background: 'rgba(245,208,97,0.06)', borderLeft: '3px solid #C5960C' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: C.textPrimary, fontSize: 14 }}>
                      Party: {row.party_name || '(unnamed party)'}
                    </div>
                    <div style={{ fontSize: 11, color: '#92670D', marginTop: 4, fontWeight: 600 }}>
                      Party-level aggregate: {row.aggregate_count} {bucketLabel.toLowerCase().slice(0, -1)}
                      {row.aggregate_count !== 1 ? (bucket === 'adults' ? 's' : 'ren') : ''}
                    </div>
                    <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, lineHeight: 1.5 }}>
                      <code style={{ background: 'rgba(245,208,97,0.20)', color: '#92670D', padding: '1px 6px', borderRadius: 3, fontWeight: 600 }}>source: party rsvp_logistics</code>
                      <span> — exact per-person age is not stored at this level.</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 3, background: 'rgba(197,150,12,0.15)', color: '#92670D', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
                    Aggregate
                  </span>
                </div>
                {row.party_members && row.party_members.length > 0 && (
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed rgba(0,0,0,0.10)' }}>
                    <div style={{ fontSize: 10, color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>
                      Members of this party (NOT a one-to-one mapping to the count above):
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, listStyle: 'disc', color: C.textSecondary }}>
                      {row.party_members.map(m => (
                        <li key={m.guest_id} style={{ fontSize: 12, lineHeight: 1.7 }}>
                          {m.name}
                          {m.age_group ? <span style={{ color: C.textMuted }}> (age_group on file: {m.age_group})</span> : <span style={{ color: C.textMuted, fontStyle: 'italic' }}> (no age_group on file)</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   RsvpListModal — host-facing RSVP Yes/No drilldown (May 2026)
   --------------------------------------------------------------------------
   Quick-answer modal for the RSVP Yes / RSVP No rings on the dashboard.
   Strictly host-facing wording — no source labels, no aggregate jargon,
   no pills, no debug strings.

   Data sources (existing endpoints, no backend change):
     • GET /api/guests   — guest rsvp_status, age_group, party_id, party_name
     • GET /api/parties  — party-level rsvp_logistics for parties that
                           submitted at the party level (no per-person ages).

   Per-party summary rule:
     1) If the party has rsvp_logistics with adults_count/children_count and
        the per-guest age data isn't reliable → "{N} adults" or
        "{A} adults · {C} children" derived from logistics.
     2) Else, count age_group on individual guests in that party
        with the matching status. Same wording.
     3) If neither yields anything (rare edge), say "Submitted at party level".
   ═══════════════════════════════════════════════════════════════════════════ */
function RsvpListModal({ status, scope = 'all', loading, parties, errorMsg, onClose, onViewInGuests }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const partiesGrouped = Array.isArray(parties) ? parties : [];
  const totalGuests = partiesGrouped.reduce((s, p) => s + (p.guests || []).length, 0);
  const totalChildren = partiesGrouped.reduce((s, p) => s + (Number(p.childrenCount) || 0), 0);
  const totalParties = partiesGrouped.length;
  const isChildrenScope = scope === 'children' && status === 'yes';
  const titleLabel = isChildrenScope
    ? 'RSVP Yes · Children'
    : (status === 'yes' ? 'RSVP Yes' : 'RSVP No');
  let subtitle = null;
  if (totalParties > 0) {
    if (isChildrenScope) {
      subtitle = `${totalChildren} ${totalChildren === 1 ? 'child' : 'children'} · ${totalParties} ${totalParties === 1 ? 'party' : 'parties'}`;
    } else {
      subtitle = `${totalGuests} ${totalGuests === 1 ? 'guest' : 'guests'} · ${totalParties} ${totalParties === 1 ? 'party' : 'parties'}`;
    }
  }
  let emptyCopy;
  if (isChildrenScope) emptyCopy = 'No children among the confirmed yeses yet.';
  else if (status === 'yes') emptyCopy = 'No confirmed yeses yet.';
  else emptyCopy = 'No declines so far.';

  return (
    <div
      data-testid="rsvp-list-modal-backdrop"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'rgba(20,15,8,0.45)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        data-testid="rsvp-list-modal"
        style={{
          width: 600, maxWidth: '94vw', maxHeight: '86vh',
          display: 'flex', flexDirection: 'column',
          background: 'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(252,250,245,0.99) 100%)',
          borderRadius: 18,
          border: '1px solid rgba(212,175,55,0.20)',
          boxShadow: '0 32px 96px rgba(0,0,0,0.22)',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 26px 16px',
          borderBottom: '1px solid rgba(0,0,0,0.06)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10,
              textTransform: 'uppercase', letterSpacing: '0.14em',
              color: C.textSecondary, marginBottom: 4,
            }}>
              Drilldown
            </div>
            <div style={{
              fontFamily: FONT.playfair, fontWeight: 700, fontSize: 22,
              color: C.textPrimary, lineHeight: 1.2,
            }}>
              {titleLabel}
            </div>
            {subtitle && (
              <div style={{
                fontFamily: FONT.cormorant, fontSize: 13, fontStyle: 'italic',
                color: C.textSecondary, marginTop: 4,
              }}>
                {subtitle}
              </div>
            )}
          </div>
          <button
            data-testid="rsvp-list-modal-close"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: 999, width: 30, height: 30, cursor: 'pointer',
              fontSize: 18, color: C.textSecondary, lineHeight: 1, padding: 0,
              flexShrink: 0,
            }}
          >×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {loading && (
            <div style={{
              padding: '40px 24px', textAlign: 'center',
              fontFamily: FONT.cormorant, fontSize: 13, fontStyle: 'italic',
              color: C.textSecondary,
            }}>
              Loading…
            </div>
          )}

          {!loading && errorMsg && (
            <div data-testid="rsvp-list-modal-error" style={{
              margin: '14px 24px', padding: '10px 14px', borderRadius: 10,
              background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.25)',
              fontFamily: FONT.cormorant, fontSize: 13, color: C.statusRedDark,
            }}>
              {errorMsg}
            </div>
          )}

          {!loading && !errorMsg && partiesGrouped.length === 0 && (
            <div data-testid="rsvp-list-modal-empty" style={{
              padding: '48px 24px', textAlign: 'center',
              fontFamily: FONT.cormorant, fontSize: 14, fontStyle: 'italic',
              color: C.textSecondary,
            }}>
              {emptyCopy}
            </div>
          )}

          {!loading && !errorMsg && partiesGrouped.map((grp, idx) => {
            // For party-level submissions in children scope, the per-guest
            // age data isn't available — surface the rsvp_logistics-derived
            // children count as a small hint line under the guest names so
            // the host still sees the truthful "N child(ren) in this group"
            // signal without us fabricating which named guest is the child.
            const showPartyLevelChildHint =
              isChildrenScope && grp.submittedAtPartyLevel && grp.childrenCount > 0;
            return (
              <div
                key={grp.party_id || idx}
                data-testid={`rsvp-list-modal-party-${idx}`}
                style={{
                  padding: '14px 26px',
                  borderBottom: idx === partiesGrouped.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 6 }}>
                  <span style={{
                    flex: 1, minWidth: 0,
                    fontFamily: FONT.playfair, fontWeight: 700, fontSize: 15,
                    color: C.textPrimary,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {grp.party_name}
                  </span>
                  <span style={{
                    flexShrink: 0,
                    fontFamily: FONT.cormorant, fontSize: 12,
                    color: C.textSecondary, fontStyle: 'italic',
                  }}>
                    {grp.summary}
                  </span>
                </div>
                {/* Guest list — grouped by Adults/Children when per-guest age
                    data is available; flat list when this party submitted at
                    the party level (we don't fabricate which named guest is
                    the child — the hint line below carries that truth). */}
                {grp.submittedAtPartyLevel ? (
                  <div style={{
                    fontFamily: FONT.cormorant, fontSize: 13.5,
                    color: C.textPrimary, lineHeight: 1.55,
                  }}>
                    {(grp.guests || []).map((g) => g.name).join(', ')}
                  </div>
                ) : (() => {
                  const adultsList = (grp.guests || []).filter((g) => !g.isChild);
                  const childrenList = (grp.guests || []).filter((g) => g.isChild);
                  const sectionLabelStyle = {
                    fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 9.5,
                    textTransform: 'uppercase', letterSpacing: '0.12em',
                    color: C.textSecondary, marginBottom: 2,
                  };
                  const nameRowStyle = {
                    fontFamily: FONT.cormorant, fontSize: 13.5,
                    color: C.textPrimary, lineHeight: 1.55,
                  };
                  return (
                    <div>
                      {adultsList.length > 0 && (
                        <div data-testid={`rsvp-list-modal-adults-${idx}`} style={{ marginBottom: childrenList.length > 0 ? 8 : 0 }}>
                          <div style={sectionLabelStyle}>Adults</div>
                          {adultsList.map((g, ai) => (
                            <div key={`${grp.party_id}-a-${ai}`} style={nameRowStyle}>{g.name}</div>
                          ))}
                        </div>
                      )}
                      {childrenList.length > 0 && (
                        <div data-testid={`rsvp-list-modal-children-${idx}`}>
                          <div style={sectionLabelStyle}>Children</div>
                          {childrenList.map((g, ci) => (
                            <div key={`${grp.party_id}-c-${ci}`} style={{ ...nameRowStyle, display: 'flex', alignItems: 'center', gap: 0, flexWrap: 'wrap' }}>
                              <span>{g.name}</span>
                              <span
                                data-testid={`rsvp-list-modal-child-tag-${idx}-${ci}`}
                                style={{
                                  display: 'inline-block',
                                  marginLeft: 6,
                                  padding: '1px 7px',
                                  borderRadius: 999,
                                  border: `1px solid ${C.goldPrimary}55`,
                                  background: 'rgba(212,175,55,0.08)',
                                  fontFamily: FONT.cormorant,
                                  fontSize: 9.5,
                                  fontStyle: 'italic',
                                  fontWeight: 700,
                                  letterSpacing: '0.10em',
                                  textTransform: 'uppercase',
                                  color: C.goldDark,
                                  verticalAlign: '0.08em',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                Child
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })()}
                {showPartyLevelChildHint && (
                  <div
                    data-testid={`rsvp-list-modal-party-child-hint-${idx}`}
                    style={{
                      marginTop: 6,
                      fontFamily: FONT.cormorant, fontSize: 11.5, fontStyle: 'italic',
                      color: C.textSecondary, letterSpacing: '0.01em',
                    }}
                  >
                    Includes {grp.childrenCount} {grp.childrenCount === 1 ? 'child' : 'children'} (party-level RSVP — not individually identified)
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 26px',
          borderTop: '1px solid rgba(0,0,0,0.06)',
          background: 'rgba(252,250,245,0.6)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
        }}>
          <span style={{
            fontFamily: FONT.cormorant, fontSize: 11, fontStyle: 'italic',
            color: C.textMuted,
          }}>
            Esc to close
          </span>
          <button
            data-testid="rsvp-list-modal-view-in-guests"
            onClick={onViewInGuests}
            style={{
              padding: '9px 18px', borderRadius: 999,
              background: `linear-gradient(145deg, ${C.goldPrimary}, ${C.goldDark})`,
              border: 'none',
              fontFamily: FONT.system, fontSize: 11, fontWeight: 800,
              color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase',
              cursor: 'pointer',
              boxShadow: `0 6px 16px ${C.goldPrimary}33, inset 0 1px 0 rgba(255,255,255,0.35)`,
              transition: 'transform 120ms, box-shadow 120ms',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-1px)';
              e.currentTarget.style.boxShadow = `0 8px 22px ${C.goldPrimary}44, inset 0 1px 0 rgba(255,255,255,0.45)`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = '';
              e.currentTarget.style.boxShadow = `0 6px 16px ${C.goldPrimary}33, inset 0 1px 0 rgba(255,255,255,0.35)`;
            }}
          >
            View in Guests
          </button>
        </div>
      </div>
    </div>
  );
}



/* ═══════════════════════════════════════════════════════════════════════════
   ROW 3 — ALERTS + INVITE FUNNEL (Funnel left, Alerts right; gap on far left)
   ROW 3b — EVENT READINESS TIMELINE (full-width)
   ═══════════════════════════════════════════════════════════════════════════ */
function AlertsFunnelRow({ guestStats, unreadCount, onNavigate }) {
  // Layout: [Words for Carol 4fr | Funnel 4fr | Alerts 4fr]
  // Words for Carol sits to the LEFT of Invite Open Funnel by design,
  // below Attendance Overview and above the Event Readiness Timeline.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, alignItems: 'stretch' }}>
      <WordsForCarolWidget />
      <InviteFunnelWidget guestStats={guestStats} onNavigate={onNavigate} />
      <div id="priority-alerts-anchor" style={{ scrollMarginTop: 20 }}>
        <PriorityAlerts guestStats={guestStats} unreadCount={unreadCount} onNavigate={onNavigate} />
      </div>
    </div>
  );
}

function TimelineRow() {
  // HONEST FRAMING NOTE — these milestones are a fixed *planning calendar* of key
  // deadlines (see TIMELINE_MILESTONE_DEFS). The only signal we have here is
  // whether each deadline date has passed vs. is still ahead — we do NOT track
  // whether the underlying task was actually completed. So this row presents a
  // schedule ("date passed" / "due soon" / "upcoming"), NOT a fabricated
  // completion/readiness percentage. `status === 'completed'` from
  // computeMilestoneStatus literally means "deadline date is in the past".
  const total = TIMELINE_MILESTONES.length;
  const datePassed = TIMELINE_MILESTONES.filter(m => m.status === 'completed').length;
  const upcoming = total - datePassed;
  // Next deadline still ahead on the calendar (first non-passed milestone).
  const nextMilestone = TIMELINE_MILESTONES.find(m => m.status !== 'completed') || null;
  const [hoveredIdx, setHoveredIdx] = useState(null);

  return (
    <div style={{ ...glassGold, padding: 20, display: 'flex', flexDirection: 'column' }}>
      <TopShine />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block' }}>Planning Schedule</span>
          <span style={{ fontFamily: FONT.system, fontSize: 10, color: C.textMuted, display: 'block', marginTop: 2 }}>Key deadline dates on the planning calendar</span>
        </div>
        <div style={{ display: 'flex', gap: 14 }}>
          {[{ l: 'Date passed', c: C.goldPrimary }, { l: 'Upcoming', c: '#d1cbc0' }].map(x => (
            <div key={x.l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <div style={{ width: 8, height: 8, borderRadius: 4, background: x.c }} />
              <span style={{ fontSize: 10, color: C.textSecondary }}>{x.l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Timeline track + nodes */}
      <div style={{ position: 'relative', padding: '20px 0 10px' }}>
        {/* Background track */}
        <div style={{ position: 'absolute', top: 33, left: 20, right: 20, height: 4, background: 'rgba(0,0,0,0.06)', borderRadius: 2 }} />
        {/* Gold fill — marks how far along the calendar we are (share of deadline
            dates already passed), NOT how much work is done. */}
        <div style={{ position: 'absolute', top: 33, left: 20, height: 4, borderRadius: 2, width: `${safePct(datePassed, total) * 0.9}%`, background: GRAD.goldTrack, boxShadow: '0 0 12px rgba(212,175,55,0.4), 0 0 4px rgba(212,175,55,0.6)' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          {TIMELINE_MILESTONES.map((m, i) => {
            // isPast = this deadline DATE is in the past (computeMilestoneStatus).
            // It does NOT mean the task is finished — so we render a neutral
            // "date reached" node, not a ✓ completion checkmark.
            const isPast = m.status === 'completed';
            const isNow = m.status === 'active' && TIMELINE_MILESTONES.slice(0, i).every(mm => mm.status === 'completed');
            return (
              <div key={m.id} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, position: 'relative', cursor: 'pointer' }}
                onMouseEnter={() => setHoveredIdx(i)} onMouseLeave={() => setHoveredIdx(null)}>
                {/* Pulse ring for the nearest upcoming deadline */}
                {isNow && <div style={{ position: 'absolute', top: -1, width: 44, height: 44, borderRadius: '50%', border: '2px solid rgba(212,175,55,0.25)', animation: 'dashPulse 2s infinite' }} />}
                {/* Node */}
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2,
                  background: isPast ? `linear-gradient(145deg, ${C.goldPrimary}, ${C.goldDark})` : isNow ? `linear-gradient(145deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))` : '#fff',
                  border: isPast ? 'none' : isNow ? `2px solid ${C.goldPrimary}` : '2px solid #e8e4dc',
                  boxShadow: isPast ? `0 4px 12px ${C.goldPrimary}40` : 'none',
                }}>
                  {isPast && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '50%', borderRadius: '14px 14px 0 0', background: 'linear-gradient(180deg, rgba(255,255,255,0.22) 0%, transparent 100%)' }} />}
                  {isPast ? <Circle size={9} color="#fff" strokeWidth={0} fill="#fff" /> : isNow ? <Zap size={12} color={C.goldPrimary} /> : <Circle size={8} color="#d1cbc0" />}
                </div>
                <span style={{ fontSize: 10, fontWeight: 700, color: isPast ? C.textPrimary : isNow ? C.goldDark : C.textMuted, marginTop: 8, textAlign: 'center' }}>{m.title}</span>
                <span style={{ fontSize: 9, color: C.textMuted }}>{m.label} '26</span>

                {/* Tooltip — shows the task + an honest date-status line */}
                {hoveredIdx === i && (
                  <div style={{ position: 'absolute', top: -64, left: '50%', transform: 'translateX(-50%)', background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(16px)', border: '1.5px solid rgba(212,175,55,0.2)', borderRadius: 10, boxShadow: SHADOW.tooltip, padding: '8px 12px', zIndex: 50, pointerEvents: 'none', minWidth: 160 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: C.textPrimary, display: 'block' }}>{m.title}</span>
                    <span style={{ fontSize: 10, color: C.textSecondary, lineHeight: '13.5px', display: 'block', maxWidth: 200 }}>{m.task}</span>
                    <span style={{ fontSize: 9, color: isPast ? C.goldDark : C.textMuted, fontWeight: 700, display: 'block', marginTop: 4 }}>
                      {isPast ? `Due date passed · ${m.deadline}` : `Due ${m.deadline}`}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Schedule panel — date-grounded facts only. We report the next deadline
          and how many deadline DATES have passed; we do NOT claim a readiness /
          completion percentage we can't actually measure. The gauge tracks
          "schedule elapsed" (share of dates passed), explicitly labeled. */}
      <div style={{ marginTop: 'auto', background: 'linear-gradient(135deg, rgba(212,175,55,0.05), rgba(212,175,55,0.02))', borderRadius: 14, border: '1.5px solid rgba(212,175,55,0.15)', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CalendarIcon size={14} color={C.goldPrimary} />
          <span style={{ fontSize: 11, color: C.textSecondary }}>
            {nextMilestone
              ? <>Next deadline: <b style={{ color: C.textPrimary }}>{nextMilestone.title}</b> · {nextMilestone.deadline}</>
              : 'All scheduled deadlines have passed'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary }}>Deadlines Passed</span>
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 17, color: C.textPrimary }}>{datePassed}/{total}</span>
        </div>
        {/* Schedule-elapsed gauge — share of deadline dates already passed. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width={72} height={72} viewBox="0 0 72 72">
            <circle cx="36" cy="36" r="28" fill="none" stroke="#e8e4dc" strokeWidth="8" />
            <circle cx="36" cy="36" r="28" fill="none" strokeWidth="8" strokeLinecap="round"
              stroke={C.goldPrimary}
              strokeDasharray={`${(safePct(datePassed, total) / 100) * 175.9} ${175.9 - (safePct(datePassed, total) / 100) * 175.9}`}
              strokeDashoffset={175.9 / 4} style={{ transition: 'stroke-dasharray 600ms' }} />
            <text x="36" y="40" textAnchor="middle" fill={C.textPrimary} fontSize="16" fontWeight="700" fontFamily={FONT.playfair}>{safePct(datePassed, total)}%</text>
          </svg>
          <div>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary, display: 'block' }}>Schedule Elapsed</span>
            <span style={{ fontSize: 9, color: C.textMuted, display: 'block', maxWidth: 120, lineHeight: 1.3 }}>{upcoming} deadline{upcoming === 1 ? '' : 's'} still ahead</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const ALERT_SEVERITY = {
  critical: { label: 'CRITICAL', bg: 'linear-gradient(135deg, rgba(220,38,38,0.06), rgba(220,38,38,0.02))', barColor: C.statusRed, Icon: AlertCircle, iconAccent: C.statusRed, iconEnd: C.statusRedDark },
  warning: { label: 'WARNING', bg: 'linear-gradient(135deg, rgba(245,158,11,0.06), rgba(245,158,11,0.02))', barColor: C.statusAmber, Icon: AlertTriangle, iconAccent: C.statusAmber, iconEnd: C.statusAmberDark },
  info: { label: 'INFO', bg: 'linear-gradient(135deg, rgba(37,99,235,0.06), rgba(37,99,235,0.02))', barColor: C.chartBlue, Icon: Info, iconAccent: C.chartBlue, iconEnd: '#1D4ED8' },
};

function PriorityAlerts({ guestStats, unreadCount, onNavigate }) {
  const [dismissed, setDismissed] = useState([]);
  const [attentionAlerts, setAttentionAlerts] = useState([]);
  const [resolving, setResolving] = useState(null);
  const fetchAlerts = useCallback(() => {
    fetch(`${API}/api/attention-items`).then(r => r.json()).then(resp => {
      const all = Array.isArray(resp) ? resp : (resp.items || []);
      const critical = all.filter(i => (i.reason_code === 'ADDITIONAL_GUEST_REQUEST' || i.reason_code === 'UNRESTRICTED_GUEST_REVIEW') && !i.resolved);
      const updated = all.filter(i => i.reason_code === 'GROUPED_RSVP_UPDATED' && !i.resolved);
      const mapped = [
        ...critical.map(i => ({
          severity: 'critical',
          message: i.display_message || i.title || 'Additional guest request pending',
          tab: 'guests',
          itemId: i.id,
          partyId: i.deep_link_params?.partyId || i.entity_id,
          actionRequired: true,
        })),
        ...updated.map(i => ({
          severity: 'info',
          message: i.display_message || 'Grouped RSVP updated',
          tab: 'guests',
          itemId: i.id,
          partyId: i.deep_link_params?.partyId || i.entity_id,
          actionRequired: false,
          quietUpdate: true,
        })),
      ];
      setAttentionAlerts(mapped);
    }).catch(() => {});
  }, []);
  useEffect(() => { fetchAlerts(); }, [fetchAlerts]);

  const handleResolve = async (itemId) => {
    setResolving(itemId);
    try {
      await fetch(`${API}/api/attention-items/${itemId}/resolve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ resolution_note: 'Resolved by host from Dashboard' }),
      });
      fetchAlerts();
    } catch {}
    setResolving(null);
  };

  const alerts = [];
  if (guestStats.failed > 0) alerts.push({ severity: 'critical', message: `${guestStats.failed} invitation delivery failed — resend required`, tab: 'notices' });
  if (unreadCount > 0) alerts.push({ severity: 'warning', message: `${unreadCount} unread guest messages awaiting response`, tab: 'notices' });
  if (guestStats.total > 0 && guestStats.pending > guestStats.total * 0.4) alerts.push({ severity: 'critical', message: `Pending RSVPs exceeding 40% threshold`, tab: 'guests' });
  attentionAlerts.forEach(a => alerts.push(a));
  const visible = alerts.filter((_, i) => !dismissed.includes(i));
  const criticalCount = visible.filter(a => a.severity === 'critical').length;

  return (
    <div style={{ ...glassStd, padding: 20, display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 400, overflow: 'hidden' }}>
      <TopShine />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexShrink: 0 }}>
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary }}>Priority Alerts</span>
        {criticalCount > 0 && <SharpBadge text={`${criticalCount} Critical`} bg={`linear-gradient(135deg, ${C.statusRed}, ${C.statusRedDark})`} color="#fff" />}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {visible.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, gap: 8, padding: 20 }}>
            <CheckCircle size={28} color={C.statusGreen} />
            <span style={{ fontSize: 12, color: C.textSecondary }}>No active alerts</span>
          </div>
        ) : visible.map((alert, i) => {
          const cfg = ALERT_SEVERITY[alert.severity] || ALERT_SEVERITY.info;
          return (
            <div key={i} style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', background: cfg.bg, border: `1px solid ${cfg.barColor}15` }}>
              <div style={{ width: 3, background: cfg.barColor, flexShrink: 0 }} />
              <div style={{ flex: 1, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                <Icon3D Icon={cfg.Icon} size={28} radius={8} accent={cfg.iconAccent} accentEnd={cfg.iconEnd} />
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: 11, fontWeight: 500, color: C.textPrimary, display: 'block', lineHeight: '15px', marginBottom: 4 }}>{alert.message}</span>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <SharpBadge text={alert.quietUpdate ? 'UPDATED' : cfg.label} bg={`${cfg.barColor}12`} color={cfg.barColor} />
                    {alert.actionRequired && (
                      <>
                        <button onClick={() => onNavigate(alert.tab)} style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, border: `1px solid ${C.statusGreen}40`, background: `${C.statusGreen}0A`, color: C.statusGreen, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          View Party
                        </button>
                        <button
                          data-testid={`resolve-alert-${alert.itemId}`}
                          onClick={() => handleResolve(alert.itemId)}
                          disabled={resolving === alert.itemId}
                          style={{ fontSize: 9, fontWeight: 700, padding: '2px 8px', borderRadius: 4, border: `1px solid ${C.goldPrimary}40`, background: `${C.goldPrimary}0A`, color: C.goldPrimary, cursor: resolving === alert.itemId ? 'not-allowed' : 'pointer', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: resolving === alert.itemId ? 0.5 : 1 }}>
                          {resolving === alert.itemId ? 'Resolving...' : 'Mark Resolved'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
                {!alert.actionRequired && (
                  <button
                    data-testid={alert.quietUpdate ? `dismiss-updated-${alert.itemId}` : undefined}
                    onClick={() => {
                      if (alert.quietUpdate && alert.itemId) {
                        handleResolve(alert.itemId);
                      } else {
                        setDismissed(p => [...p, i]);
                      }
                    }}
                    style={{ width: 18, height: 18, borderRadius: 4, background: 'rgba(0,0,0,0.04)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <X size={10} color={C.textMuted} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROW 4 — FINANCIAL HUB
   ═══════════════════════════════════════════════════════════════════════════ */
function FinancialRow({ vendors, guestStats, budget, liquidity, onNavigate }) {
  const allVendors = vendors ?? [];
  const adults = guestStats?.adults_attending ?? guestStats?.rsvp_yes ?? 0;
  const children = guestStats?.children_attending ?? 0;
  const totalHeadcount = adults + children;
  const totalBudget = budget?.total_budget ?? 0;
  const spent = budget?.spent_budget ?? 0;
  const remaining = Math.max(0, budget?.available_budget ?? (totalBudget - spent));
  const cashReceived = liquidity?.cash_received ?? 0;
  const vendorObligation = allVendors.reduce((s, v) => s + Math.max(0, (v.contract_total ?? 0) - (v.paid_to_date ?? 0)), 0);
  const costPerGuest = totalHeadcount > 0 ? Math.round(spent / totalHeadcount) : 0;
  const vendorsPaid = allVendors.filter(v => (v.paid_to_date ?? 0) >= (v.contract_total ?? 1)).length;
  const vendorHealth = safePct(vendorsPaid, allVendors.length || 1);

  // Preserved financial logic
  const evalRule = (op, count, threshold) => { if (op === '>') return count > threshold; if (op === '<') return count < threshold; if (op === '>=') return count >= threshold; if (op === '<=') return count <= threshold; if (op === '=') return count === threshold; if (op === '!=') return count !== threshold; return false; };
  const getCountValue = (field) => { if (field === 'child_count') return children; if (field === 'total_headcount') return totalHeadcount; return adults; };

  let grandTotal = 0;
  const vendorRows = allVendors.map(v => {
    const pc = v.pricing_conditions;
    const hasC = pc && pc.adult_rate != null && pc.adult_operator && pc.adult_threshold != null;
    if (hasC) {
      const acv = getCountValue(pc.adult_count_field || 'adult_count');
      const aa = evalRule(pc.adult_operator, acv, pc.adult_threshold);
      if (aa) {
        const ac = adults * pc.adult_rate;
        let cc = 0;
        if (pc.child_rate != null && pc.child_operator && pc.child_threshold != null) {
          const ccv = getCountValue(pc.child_count_field || 'adult_count');
          const ca = evalRule(pc.child_operator, ccv, pc.child_threshold);
          cc = children * (ca ? pc.child_rate : pc.adult_rate);
        }
        const vt = ac + cc; grandTotal += vt; return { vendor: v, vendorTotal: vt };
      }
    }
    const vt = v.contract_total || 0; grandTotal += vt; return { vendor: v, vendorTotal: vt };
  });

  // Donut SVG
  const donutR = 65, donutC = 2 * Math.PI * donutR;
  const segments = [];
  const categories = {};
  allVendors.forEach(v => { const cat = v.role_category || 'Other'; categories[cat] = (categories[cat] || 0) + (v.contract_total || 0); });
  const catEntries = Object.entries(categories).sort((a, b) => b[1] - a[1]);
  const catColors = [C.goldPrimary, C.chartBlue, C.chartPurple, C.statusAmber, C.chartPink, C.goldDark, C.statusGreen];
  let offset = donutC / 4;
  catEntries.forEach(([name, val], i) => {
    const pct = grandTotal > 0 ? (val / grandTotal) * 100 : 0;
    const len = (pct / 100) * donutC;
    segments.push({ name, pct: Math.round(pct), len, offset, color: catColors[i % catColors.length] });
    offset -= len;
  });

  const spentW = totalBudget > 0 ? Math.min(100, (spent / totalBudget) * 100) : 0;

  const statusBadge = (s) => {
    const map = { Confirmed: { bg: `linear-gradient(135deg, ${C.statusGreen}, ${C.statusGreenDark})`, c: '#fff', t: 'PAID' }, Booked: { bg: `linear-gradient(135deg, ${C.statusGreen}, ${C.statusGreenDark})`, c: '#fff', t: 'PAID' }, Considering: { bg: `linear-gradient(135deg, ${C.statusAmber}, ${C.statusAmberDark})`, c: '#fff', t: 'PENDING' } };
    const m = map[s] || { bg: `linear-gradient(135deg, ${C.statusAmber}, ${C.statusAmberDark})`, c: '#fff', t: s?.toUpperCase() || 'PARTIAL' };
    return <span style={{ display: 'inline-block', width: 68, textAlign: 'center', padding: '3px 0', borderRadius: 0, background: m.bg, color: m.c, fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{m.t}</span>;
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '5fr 7fr', gap: 16 }}>
      {/* Financial Overview */}
      <div style={{ ...glassGold, padding: 20 }}>
        <TopShine />
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block', marginBottom: 16 }}>Financial Overview</span>

        {/* Runway bar — 4 available values */}
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 8 }}>Financial Runway</span>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: C.goldPrimary }} /><span style={{ fontSize: 10, fontWeight: 600, color: C.textSecondary }}>Spent {fmt(spent)}</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: C.goldDark }} /><span style={{ fontSize: 10, fontWeight: 600, color: C.textSecondary }}>Committed {fmt(vendorObligation)}</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: C.statusGreen }} /><span style={{ fontSize: 10, fontWeight: 600, color: C.textSecondary }}>Received {fmt(cashReceived)}</span></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 10, height: 10, borderRadius: 2, background: '#e0dbd2' }} /><span style={{ fontSize: 10, fontWeight: 600, color: C.textSecondary }}>Budget {fmt(totalBudget)}</span></div>
        </div>
        <div style={{ height: 32, background: '#e8e4dc', borderRadius: 5, overflow: 'hidden', marginBottom: 20, boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)', display: 'flex', position: 'relative' }}>
          {/* Spent segment */}
          <div style={{ width: `${totalBudget > 0 ? Math.min(100, (spent / totalBudget) * 100) : 0}%`, background: GRAD.goldBar, borderRadius: '5px 0 0 5px', transition: 'width 500ms', boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)', zIndex: 3 }} />
          {/* Committed segment */}
          <div style={{ width: `${totalBudget > 0 ? Math.min(100 - (spent / totalBudget) * 100, (vendorObligation / totalBudget) * 100) : 0}%`, background: `linear-gradient(90deg, ${C.goldDark}, ${C.goldDeep})`, opacity: 0.7, transition: 'width 500ms', zIndex: 2 }} />
          {/* Cash received marker line */}
          {cashReceived > 0 && totalBudget > 0 && (
            <div style={{ position: 'absolute', left: `${Math.min(100, (cashReceived / totalBudget) * 100)}%`, top: 0, bottom: 0, width: 2, background: C.statusGreen, zIndex: 4, boxShadow: `0 0 6px ${C.statusGreen}60` }} />
          )}
        </div>

        {/* Donut */}
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 8 }}>Budget Allocation</span>
        <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', perspective: 600 }}>
          <div style={{ transform: 'rotateX(20deg)', position: 'relative' }}>
            {/* Shadow layer */}
            <div style={{ position: 'absolute', top: 8, left: 4, right: 4, bottom: -8, borderRadius: '50%', background: 'rgba(0,0,0,0.18)', filter: 'blur(12px)' }} />
            <svg width={160} height={160} viewBox="0 0 160 160" style={{ position: 'relative', zIndex: 1 }}>
              <circle cx="80" cy="80" r={donutR} fill="none" stroke="#f0ede8" strokeWidth="26" />
              {segments.map((seg, i) => (
                <circle key={i} cx="80" cy="80" r={donutR} fill="none" stroke={seg.color} strokeWidth="26"
                  strokeDasharray={`${seg.len - 3} ${donutC - seg.len + 3}`} strokeDashoffset={seg.offset}
                  style={{ transition: 'stroke-dasharray 600ms' }} />
              ))}
              <text x="80" y="75" textAnchor="middle" fill={C.textPrimary} fontSize="20" fontWeight="700" fontFamily={FONT.playfair}>{fmt(grandTotal)}</text>
              <text x="80" y="92" textAnchor="middle" fill={C.textSecondary} fontSize="9" fontWeight="600" textTransform="uppercase">TOTAL</text>
            </svg>
          </div>
        </div>
        {/* Segment labels */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginTop: 8 }}>
          {segments.map(seg => (
            <div key={seg.name} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: seg.color }} />
              <span style={{ fontFamily: FONT.cormorant, fontSize: 10, color: C.textSecondary }}>{seg.name} {seg.pct}%</span>
            </div>
          ))}
        </div>

        {/* Budget Allocation Status — current composition of the budget from live
            figures (no fabricated trend line). A true day-by-day burn-down needs
            a historical spend ledger we do not store, so we show the real
            present-state split instead of inventing a curve. */}
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', margin: '16px 0 8px' }}>Budget Status</span>
        {totalBudget > 0 ? (() => {
          const committedNotSpent = Math.max(0, Math.min(vendorObligation, totalBudget - spent));
          const free = Math.max(0, totalBudget - spent - committedNotSpent);
          const pctOf = (n) => (totalBudget > 0 ? (n / totalBudget) * 100 : 0);
          return (
            <>
              <div data-testid="budget-status-bar" style={{ display: 'flex', height: 18, borderRadius: 5, overflow: 'hidden', background: '#e8e4dc', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)' }}>
                {spent > 0 && <div style={{ width: `${pctOf(spent)}%`, background: GRAD.goldBar }} title={`Spent ${fmt(spent)}`} />}
                {committedNotSpent > 0 && <div style={{ width: `${pctOf(committedNotSpent)}%`, background: `linear-gradient(90deg, ${C.goldDark}, ${C.goldDeep})`, opacity: 0.7 }} title={`Committed (unpaid) ${fmt(committedNotSpent)}`} />}
                {free > 0 && <div style={{ flex: 1 }} title={`Unallocated ${fmt(free)}`} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontFamily: FONT.system, fontSize: 9.5, color: C.textSecondary, fontWeight: 600 }}>
                <span>Spent {fmt(spent)}</span>
                <span>Committed {fmt(committedNotSpent)}</span>
                <span>Free {fmt(free)}</span>
              </div>
            </>
          );
        })() : (
          <div data-testid="budget-status-empty" style={{ fontSize: 10, color: C.textSecondary, fontStyle: 'italic', padding: '6px 0' }}>
            Set a total budget in Finance to see how it breaks down.
          </div>
        )}
      </div>

      {/* Vendor Contracts — flex layout with pinned summary */}
      <div style={{ ...glassStd, padding: 20, display: 'flex', flexDirection: 'column', minHeight: 420 }}>
        <TopShine />
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block', marginBottom: 14, flexShrink: 0 }}>Vendor Contracts & Summary</span>
        {/* Scrollable vendor list — takes all available space */}
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          {/* Header */}
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 70px', gap: 8, padding: '0 0 8px', borderBottom: '2px solid rgba(212,175,55,0.2)', position: 'sticky', top: 0, background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(10px)', zIndex: 2 }}>
            {['Vendor', 'Amount', 'Status', 'Due'].map(h => (
              <span key={h} style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary }}>{h}</span>
            ))}
          </div>
          {vendorRows.map(({ vendor, vendorTotal }) => (
            <div key={vendor.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 80px 70px', gap: 8, padding: '10px 0', borderBottom: '1px solid rgba(212,175,55,0.06)', alignItems: 'center' }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: C.textPrimary }}>{vendor.company_name}</span>
              <span style={{ fontSize: 12, fontWeight: 700, color: C.textPrimary, fontFamily: FONT.playfair }}>{fmt(vendorTotal)}</span>
              {statusBadge(vendor.vendor_status)}
              <span style={{ fontSize: 10, color: C.textMuted }}>—</span>
            </div>
          ))}
          {vendorRows.length === 0 && <p style={{ fontSize: 12, color: C.textMuted, textAlign: 'center', padding: 20 }}>No vendors added</p>}
        </div>

        {/* Summary grid — pinned to bottom */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 16, flexShrink: 0, borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 14 }}>
          {[
            { l: 'Total Spent', v: fmt(spent) }, { l: 'Budget Allowed', v: fmt(totalBudget) }, { l: 'Cash Committed', v: fmt(vendorObligation) },
            { l: 'Cash Received', v: fmt(cashReceived) }, { l: 'Cost Per Guest', v: fmt(costPerGuest) }, { l: 'Vendor Health', v: `${vendorHealth}%` },
          ].map(m => (
            <div key={m.l} style={{ background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))', borderRadius: 10, border: '1.5px solid rgba(212,175,55,0.15)', padding: '10px 12px' }}>
              <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary, display: 'block', marginBottom: 2 }}>{m.l}</span>
              <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary }}>{m.v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROW 5 — SMART INBOX + RSVP VELOCITY + ACTIVITY
   ═══════════════════════════════════════════════════════════════════════════ */
function InboxVelocityRow({ messages, vendors, guestStats, onNavigate, onDeleteMessage }) {
  const [replyOpen, setReplyOpen] = useState(null);
  const [expandedMsg, setExpandedMsg] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const unread = messages.filter(m => m.status === 'unread');
  const sorted = [...messages].sort((a, b) => (b.status === 'unread' ? 1 : 0) - (a.status === 'unread' ? 1 : 0)).slice(0, 5);
  const totalResponses = (guestStats.rsvp_yes ?? 0) + (guestStats.rsvp_no ?? 0);
  const daysElapsed = Math.max(1, Math.ceil((Date.now() - new Date('2026-02-01').getTime()) / 86400000));
  const avgPerDay = (totalResponses / daysElapsed).toFixed(1);
  const rsvpCompletion = safePct(totalResponses, guestStats.total);

  const handleAck = async (id) => { try { await fetch(`${API}/api/messages/${id}`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: 'read' }) }); } catch {} };
  const handleDelete = async (id) => { try { await fetch(`${API}/api/messages/${id}`, { method: 'DELETE' }); setDeleteConfirm(null); if (onDeleteMessage) onDeleteMessage(id); } catch {} };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '5fr 3fr 4fr', gap: 16 }}>
      {/* Inbox */}
      <div style={{ ...glassStd, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <Icon3D Icon={MessageCircle} size={30} radius={10} />
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, flex: 1 }}>Smart Inbox</span>
          {unread.length > 0 && <SharpBadge text={`${unread.length} Unread`} bg={`linear-gradient(135deg, ${C.statusRed}, ${C.statusRedDark})`} color="#fff" />}
        </div>
        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 24 }}><MessageCircle size={24} color={C.goldPrimary} style={{ opacity: 0.3 }} /><p style={{ fontSize: 12, color: C.textMuted, marginTop: 8 }}>No messages</p></div>
        ) : <div style={{ maxHeight: 340, overflowY: 'auto', paddingRight: 4 }}>{sorted.map(msg => {
          const isUnread = msg.status === 'unread';
          const isExpanded = expandedMsg === msg.id;
          const typeLabel = msg.message_type === 'question' ? 'Question' : 'Note';
          const typeBg = msg.message_type === 'question' ? `linear-gradient(135deg, ${C.chartBlue}, #1D4ED8)` : `linear-gradient(135deg, ${C.goldPrimary}, ${C.goldDark})`;
          return (
            <div key={msg.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 8px', borderRadius: 8, background: isUnread ? 'rgba(212,175,55,0.04)' : 'transparent', marginBottom: 4, transition: 'background 0.15s, box-shadow 0.15s', cursor: 'pointer' }}
              onClick={() => setExpandedMsg(isExpanded ? null : msg.id)}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.5)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = isUnread ? 'rgba(212,175,55,0.04)' : 'transparent'; e.currentTarget.style.boxShadow = 'none'; }}>
              {isUnread && <div style={{ width: 8, height: 8, borderRadius: 4, background: C.statusRed, boxShadow: `0 0 6px ${C.statusRed}40`, flexShrink: 0, marginTop: 5 }} />}
              <div style={{ flex: 1, minWidth: 0 }}>
                {/* Row 1: Name + Source badge + Timestamp */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: C.textPrimary }}>{msg.guest_name}</span>
                    <SharpBadge text={typeLabel} bg={typeBg} color="#fff" />
                  </div>
                  <span style={{ fontSize: 9, color: C.textSecondary, fontWeight: 500, whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtTime(msg.created_at)}</span>
                </div>
                {/* Row 2: Email + Phone (if available) */}
                <div style={{ display: 'flex', gap: 10, marginTop: 2 }}>
                  {msg.email && (
                    <span style={{ fontSize: 9, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Mail size={9} color={C.textMuted} />{msg.email}
                    </span>
                  )}
                  {msg.phone && (
                    <span style={{ fontSize: 9, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Phone size={9} color={C.textMuted} />{msg.phone}
                    </span>
                  )}
                </div>
                {/* Row 3: Message content */}
                {isExpanded ? (
                  <p style={{ fontSize: 11, color: C.textSecondary, margin: '4px 0 0', lineHeight: '16px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.content}</p>
                ) : (
                  <p style={{ fontSize: 11, color: C.textSecondary, margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '15px' }}>{msg.content}</p>
                )}
                {/* Row 4: Actions (visible on expand) */}
                {isExpanded && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                    {isUnread && (
                      <button onClick={e => { e.stopPropagation(); handleAck(msg.id); }}
                        style={{ fontSize: 9, fontWeight: 700, border: `1px solid ${C.statusGreen}33`, borderRadius: 6, color: C.statusGreen, background: `${C.statusGreen}08`, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                        <Eye size={9} /> Acknowledge
                      </button>
                    )}
                    <button onClick={e => { e.stopPropagation(); setDeleteConfirm(msg.id); }}
                      style={{ fontSize: 9, fontWeight: 700, border: `1px solid ${C.statusRed}33`, borderRadius: 6, color: C.statusRed, background: `${C.statusRed}08`, padding: '3px 8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Trash2 size={9} /> Delete
                    </button>
                  </div>
                )}
              </div>
              {/* Reply button */}
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <button onClick={e => { e.stopPropagation(); setReplyOpen(replyOpen === msg.id ? null : msg.id); }}
                  style={{ fontSize: 10, fontWeight: 700, border: '1.5px solid rgba(212,175,55,0.25)', borderRadius: 7, color: C.goldDark, background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(212,175,55,0.03))', padding: '4px 10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, whiteSpace: 'nowrap' }}>
                  <Send size={10} /> Reply <ChevronDown size={10} />
                </button>
                {replyOpen === msg.id && (
                  <div style={{ position: 'absolute', right: 0, top: '100%', marginTop: 4, zIndex: 50, background: 'rgba(255,255,255,0.97)', backdropFilter: 'blur(20px)', border: '1.5px solid rgba(212,175,55,0.2)', borderRadius: 10, boxShadow: SHADOW.dropdown, minWidth: 130, overflow: 'hidden' }}>
                    <button onClick={() => { setReplyOpen(null); onNavigate('notices'); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', width: '100%', fontSize: 12, color: C.textPrimary, fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <Mail size={14} color={C.goldPrimary} /> Email
                    </button>
                    <div style={{ height: 1, background: 'rgba(212,175,55,0.1)', margin: '0 12px' }} />
                    <button onClick={() => { setReplyOpen(null); onNavigate('notices'); }} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer', width: '100%', fontSize: 12, color: C.textPrimary, fontWeight: 500 }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(212,175,55,0.06)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <Smartphone size={14} color={C.goldPrimary} /> SMS
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}</div>}

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setDeleteConfirm(null)}>
            <div style={{ ...glassStd, background: 'rgba(255,255,255,0.95)', padding: 28, maxWidth: 340, textAlign: 'center', borderRadius: 16, boxShadow: SHADOW.dropdown }}
              onClick={e => e.stopPropagation()}>
              <Trash2 size={28} color={C.statusRed} style={{ marginBottom: 12 }} />
              <p style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, margin: '0 0 16px' }}>Delete Message?</p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                <button onClick={() => handleDelete(deleteConfirm)}
                  style={{ padding: '8px 20px', borderRadius: 0, border: 'none', background: `linear-gradient(135deg, ${C.statusRed}, ${C.statusRedDark})`, color: '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer', letterSpacing: '0.05em' }}>
                  YES
                </button>
                <button onClick={() => setDeleteConfirm(null)}
                  style={{ padding: '8px 20px', borderRadius: 0, border: 'none', background: `linear-gradient(135deg, ${C.statusGreen}, ${C.statusGreenDark})`, color: '#fff', fontSize: 11, fontWeight: 800, cursor: 'pointer', letterSpacing: '0.05em' }}>
                  NO
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* RSVP Velocity */}
      <div style={{ ...glassGold, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Icon3D Icon={TrendingUp} size={26} radius={8} accent={C.statusGreen} accentEnd={C.statusGreenDark} />
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 15, color: C.textPrimary }}>RSVP Velocity</span>
        </div>
        <p style={{ fontSize: 10, color: C.textSecondary, margin: '0 0 12px' }}>Track how quickly guests are responding to your invitations</p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
          {[
            { l: 'All Responses', v: String(totalResponses), Icon: Users, accent: C.goldPrimary, accentEnd: C.goldDark },
            { l: 'Avg/Day', v: avgPerDay, Icon: TrendingUp, accent: C.chartBlue, accentEnd: '#1D4ED8' },
            { l: 'Pending', v: String(guestStats.pending), Icon: Clock, accent: C.statusRed, accentEnd: C.statusRedDark },
            { l: 'Completion', v: `${rsvpCompletion}%`, Icon: Activity, accent: C.statusGreen, accentEnd: C.statusGreenDark },
          ].map(s => (
            <div key={s.l} style={{ ...glassStd, padding: '8px 10px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              <Icon3D Icon={s.Icon} size={26} radius={7} accent={s.accent} accentEnd={s.accentEnd} />
              <div>
                <span style={{ fontFamily: FONT.system, fontSize: 7, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: C.textSecondary, display: 'block' }}>{s.l}</span>
                <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 13, color: C.textPrimary }}>{s.v}</span>
              </div>
            </div>
          ))}
        </div>
        {/* Response Progress — a real, present-state split of where the guest list
            stands right now (Yes / No / Pending). A dated "responses over time"
            line needs a per-day RSVP history we do not store, so we show the
            true current breakdown rather than a fabricated trend curve. */}
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 9, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 6 }}>Response Progress</span>
        {(guestStats.total ?? 0) > 0 ? (() => {
          const yes = guestStats.rsvp_yes ?? 0;
          const no = guestStats.rsvp_no ?? 0;
          const pend = guestStats.pending ?? 0;
          const tot = guestStats.total ?? 0;
          const pctOf = (n) => (tot > 0 ? (n / tot) * 100 : 0);
          return (
            <>
              <div data-testid="response-progress-bar" style={{ display: 'flex', height: 16, borderRadius: 5, overflow: 'hidden', background: '#e8e4dc', boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.06)' }}>
                {yes > 0 && <div style={{ width: `${pctOf(yes)}%`, background: `linear-gradient(90deg, ${C.statusGreen}, ${C.statusGreenDark})` }} title={`${yes} Yes`} />}
                {no > 0 && <div style={{ width: `${pctOf(no)}%`, background: `linear-gradient(90deg, ${C.statusRed}, ${C.statusRedDark})` }} title={`${no} No`} />}
                {pend > 0 && <div style={{ flex: 1 }} title={`${pend} Pending`} />}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, fontFamily: FONT.system, fontSize: 9, color: C.textSecondary, fontWeight: 600 }}>
                <span style={{ color: C.statusGreenDark }}>Yes {yes}</span>
                <span style={{ color: C.statusRedDark }}>No {no}</span>
                <span>Pending {pend}</span>
              </div>
            </>
          );
        })() : (
          <div data-testid="response-progress-empty" style={{ fontSize: 9, color: C.textSecondary, fontStyle: 'italic', padding: '6px 0' }}>
            No invitations yet — RSVP progress will appear here.
          </div>
        )}
      </div>

      {/* Activity & Pipeline */}
      <div style={{ ...glassStd, padding: 20 }}>
        <TopShine />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
          <Icon3D Icon={Activity} size={26} radius={8} accent={C.chartPurple} accentEnd="#7C3AED" />
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 15, color: C.textPrimary }}>Activity & Pipeline</span>
        </div>
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 10 }}>Live Activity</span>
        {(messages || []).slice(0, 4).map((m, i) => (
          <div key={m.id || i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
            <div style={{ width: 8, height: 8, borderRadius: 4, marginTop: 4, flexShrink: 0, background: `radial-gradient(circle, ${[C.statusGreen, C.chartBlue, C.goldPrimary, C.chartPurple][i % 4]}, ${[C.statusGreenDark, '#1D4ED8', C.goldDark, '#7C3AED'][i % 4]})`, boxShadow: `0 0 4px ${[C.statusGreen, C.chartBlue, C.goldPrimary, C.chartPurple][i % 4]}40` }} />
            <span style={{ fontSize: 11, color: C.textPrimary, flex: 1, lineHeight: '15px' }}>{m.guest_name}: {(m.content || '').slice(0, 40)}</span>
            <span style={{ fontSize: 9, color: C.textSecondary, whiteSpace: 'nowrap', fontWeight: 500 }}>{fmtTime(m.created_at)}</span>
          </div>
        ))}
        {/* Divider */}
        <div style={{ height: 1, background: GRAD.goldDivider, margin: '12px 0' }} />
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 10 }}>Vendor Pipeline</span>
        {(vendors || []).slice(0, 4).map(v => {
          const stageMap = { Confirmed: { bg: `linear-gradient(135deg, ${C.statusGreen}, ${C.statusGreenDark})`, t: 'CONFIRMED' }, Booked: { bg: `linear-gradient(135deg, ${C.statusGreen}, ${C.statusGreenDark})`, t: 'IN PROGRESS' }, Considering: { bg: `linear-gradient(135deg, ${C.statusAmber}, ${C.statusAmberDark})`, t: 'PENDING' }, Negotiating: { bg: `linear-gradient(135deg, ${C.chartPurple}, #7C3AED)`, t: 'NEGOTIATING' } };
          const st = stageMap[v.vendor_status] || { bg: `linear-gradient(135deg, ${C.statusAmber}, ${C.statusAmberDark})`, t: v.vendor_status?.toUpperCase() || 'ACTIVE' };
          return (
            <div key={v.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: C.textPrimary }}>{v.company_name}</span>
              <span style={{ display: 'inline-block', width: 92, textAlign: 'center', padding: '3px 0', borderRadius: 0, background: st.bg, color: '#fff', fontSize: 8, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{st.t}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   ROW 6 — QUICK ACTIONS + CALENDAR
   ═══════════════════════════════════════════════════════════════════════════ */
function ActionsCalendarRow({ onNavigate }) {
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return { year: n.getFullYear(), month: n.getMonth() }; });
  const [selectedDay, setSelectedDay] = useState(null);

  const actions = [
    { label: 'Send Bulk Reminder', desc: 'Notify all pending guests', Icon: Send, accent: C.goldPrimary, accentEnd: C.goldDark, tab: 'notices' },
    { label: 'Chase Pending RSVPs', desc: 'Follow up on non-responses', Icon: Users, accent: C.statusAmber, accentEnd: C.statusAmberDark, tab: 'guests' },
    { label: 'Export Guest List', desc: 'Download as CSV', Icon: Download, accent: C.chartBlue, accentEnd: '#1D4ED8', tab: 'guests' },
    { label: 'Add Vendor', desc: 'Register new vendor', Icon: Briefcase, accent: C.chartPurple, accentEnd: '#7C3AED', tab: 'vendors' },
    { label: 'Generate Report', desc: 'Full event summary', Icon: FileText, accent: C.statusGreen, accentEnd: C.statusGreenDark, tab: 'analytics' },
    { label: 'Quick Budget Check', desc: 'Instant financial snapshot', Icon: BarChart3, accent: C.goldMid, accentEnd: C.goldDeep, tab: 'finance' },
  ];

  // Calendar
  const daysInMonth = new Date(calMonth.year, calMonth.month + 1, 0).getDate();
  const firstDayOfWeek = new Date(calMonth.year, calMonth.month, 1).getDay();
  const monthName = new Date(calMonth.year, calMonth.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const today = new Date();
  const isToday = (d) => today.getFullYear() === calMonth.year && today.getMonth() === calMonth.month && today.getDate() === d;
  const milestoneDays = TIMELINE_MILESTONES.filter(m => { const d = new Date(m.deadline); return d.getMonth() === calMonth.month && d.getFullYear() === calMonth.year; }).map(m => new Date(m.deadline).getDate());

  const prevMonth = () => setCalMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { ...p, month: p.month - 1 });
  const nextMonth = () => setCalMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { ...p, month: p.month + 1 });

  const milestones = TIMELINE_MILESTONES.filter(m => m.status !== 'completed').slice(0, 4);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '4fr 8fr', gap: 16 }}>
      {/* Quick Actions */}
      <div style={{ ...glassStd, padding: 20 }}>
        <TopShine />
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block', marginBottom: 14 }}>Quick Actions</span>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          {actions.map(a => (
            <button key={a.label} data-testid={`qa-${a.label.toLowerCase().replace(/\s+/g, '-')}`}
              onClick={() => onNavigate(a.tab)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 10px', borderRadius: 12, border: '1.5px solid rgba(212,175,55,0.12)', background: 'rgba(255,255,255,0.08)', cursor: 'pointer', transition: 'all 0.15s', textAlign: 'left' }}
              onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px ${a.accent}18`; e.currentTarget.style.borderColor = `${a.accent}35`; }}
              onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'rgba(212,175,55,0.12)'; }}>
              <Icon3D Icon={a.Icon} size={36} radius={10} accent={a.accent} accentEnd={a.accentEnd} />
              <div>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.textPrimary, display: 'block', lineHeight: '14px' }}>{a.label}</span>
                <span style={{ fontSize: 9, color: C.textSecondary }}>{a.desc}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Calendar */}
      <div style={{ ...glassGold, padding: 20, display: 'flex', gap: 16 }}>
        <TopShine />
        <div style={{ flex: 1 }}>
          <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary, display: 'block', marginBottom: 14 }}>Key Dates & Calendar</span>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 17, color: C.textPrimary }}>{monthName}</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button onClick={prevMonth} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(255,255,255,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronLeft size={14} color={C.textSecondary} /></button>
              <button onClick={nextMonth} style={{ width: 28, height: 28, borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(255,255,255,0.3)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><ChevronRight size={14} color={C.textSecondary} /></button>
            </div>
          </div>
          {/* Weekday headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '2px solid rgba(212,175,55,0.2)', paddingBottom: 6, marginBottom: 6 }}>
            {['Su', 'Mon', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
              <span key={d} style={{ textAlign: 'center', fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 11, color: C.goldDark, textTransform: 'uppercase' }}>{d}</span>
            ))}
          </div>
          {/* Day grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {Array.from({ length: firstDayOfWeek }).map((_, i) => <div key={`e${i}`} />)}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const isMilestone = milestoneDays.includes(day);
              const isTdy = isToday(day);
              const isSel = selectedDay === day;
              return (
                <div key={day} onClick={() => setSelectedDay(day)}
                  style={{
                    textAlign: 'center', padding: '6px 2px', borderRadius: 8, cursor: 'pointer',
                    background: isSel ? `linear-gradient(135deg, ${C.goldPrimary}, ${C.goldDark})` : isMilestone ? `linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.06))` : 'transparent',
                    borderBottom: isMilestone && !isSel ? `2.5px solid ${C.goldPrimary}` : '2.5px solid transparent',
                    boxShadow: isSel ? `0 4px 14px rgba(212,175,55,0.4), inset 0 1px 0 rgba(255,255,255,0.3)` : 'none',
                    transition: 'background 0.15s',
                  }}
                  onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = 'rgba(212,175,55,0.1)'; }}
                  onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = isMilestone ? 'linear-gradient(135deg, rgba(212,175,55,0.12), rgba(212,175,55,0.06))' : 'transparent'; }}>
                  <span style={{
                    fontFamily: FONT.playfair, fontSize: 16,
                    fontWeight: isMilestone ? 800 : 500,
                    color: isSel ? '#fff' : isTdy ? C.goldPrimary : C.textPrimary,
                    textShadow: isTdy && !isSel ? `0 0 6px ${C.goldPrimary}40` : 'none',
                  }}>{day}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Milestones sidebar */}
        <div style={{ width: 240, borderLeft: '1px solid rgba(212,175,55,0.1)', paddingLeft: 16 }}>
          <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.textSecondary, display: 'block', marginBottom: 12 }}>Upcoming Milestones</span>
          {milestones.map(m => {
            const color = m.status === 'active' ? C.goldPrimary : '#d1cbc0';
            return (
              <div key={m.id} style={{ display: 'flex', borderRadius: 10, overflow: 'hidden', marginBottom: 8, background: 'rgba(255,255,255,0.08)', border: '1.5px solid rgba(0,0,0,0.03)' }}>
                <div style={{ width: 3.5, background: color, flexShrink: 0 }} />
                <div style={{ padding: '10px 12px' }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: C.textPrimary, display: 'block' }}>{m.title}</span>
                  <span style={{ fontSize: 10, color: C.textSecondary, display: 'block', marginTop: 2 }}>{m.task}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, color: m.status === 'active' ? C.statusRed : C.goldPrimary, marginTop: 4, display: 'block' }}>{m.deadline}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   OSCAR AI WIDGET — Phase 3D rollup of /api/oscar/health onto the Dashboard
   Read-only. Admin-only (auth header). Click → AI → Oscar → Activity & Health.
   No formulas duplicated — pulls today's slice from the same daily series
   that powers the Phase 3C panel, so the two surfaces cannot drift.
   ═══════════════════════════════════════════════════════════════════════════ */
function OscarAIWidget({ onNavigate }) {
  const [data, setData] = useState(null);
  const [mode, setMode] = useState('basic');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const auth = adminAuthHeader();
    let cancelled = false;
    (async () => {
      try {
        // days=1 returns today's UTC slice as the final daily[] entry. Same
        // endpoint + same series the Activity & Health panel renders, so
        // the two surfaces share their source of truth.
        const [hRes, mRes] = await Promise.all([
          fetch(`${API}/api/oscar/health?days=1`, { headers: auth }),
          fetch(`${API}/api/oscar/mode`,           { headers: auth }),
        ]);
        if (!hRes.ok) throw new Error(`HTTP ${hRes.status}`);
        const h = await hRes.json();
        if (cancelled) return;
        setData(h);
        if (mRes.ok) {
          const m = await mRes.json();
          if (m?.mode) setMode(m.mode);
        }
      } catch (e) {
        if (!cancelled) setError(String(e.message || e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // "Today" = the last entry of the daily-series array (which is UTC, padded
  // by the backend so it always exists). This is the same definition the
  // Activity & Health daily bar chart uses.
  const todayRow = data && Array.isArray(data.daily) && data.daily.length > 0
    ? data.daily[data.daily.length - 1]
    : { turns: 0, llm_calls: 0, cost: 0, escalated: 0, date: '' };

  const totalTurnsWindow   = data?.summary?.total_turns ?? 0;
  const pendingUnknown     = data?.unknown_questions?.totals?.pending ?? 0;
  const topPending         = data?.unknown_questions?.top_pending?.[0] || null;
  const escalationRate     = data?.summary?.escalation_rate;  // float in [0..1] or null
  const empty              = !loading && !error && totalTurnsWindow === 0;

  const handleClick = () => {
    // Hint AITab to land on Activity & Health. Consumed and cleared on mount.
    try { sessionStorage.setItem('ai_default_subtab', 'health'); } catch (_) {}
    onNavigate('oscar');
  };

  const KeyboardActivate = (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); }
  };

  const modeLabel = mode === 'basic' ? 'Basic' : 'Enhanced';
  const modeStyle = {
    fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
    padding: '3px 8px', borderRadius: 99, background: 'rgba(34,197,94,0.10)',
    color: '#1E7344', border: '1px solid rgba(34,197,94,0.28)',
  };

  return (
    <div data-testid="dashboard-ai-widget"
      role="button" tabIndex={0}
      onClick={handleClick} onKeyDown={KeyboardActivate}
      style={{ ...glassGold, padding: '18px 22px', cursor: 'pointer',
        display: 'flex', flexDirection: 'column', gap: 12,
        transition: 'transform 0.15s ease, box-shadow 0.15s ease' }}
      onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
      onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}>
      <TopShine />

      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Icon3D Icon={Activity} size={36} radius={10} accent={C.goldPrimary} accentEnd={C.goldDark} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16,
              color: C.textPrimary, letterSpacing: '-0.005em' }}>
              Oscar AI
            </span>
            <SharpBadge text="Phase 3C · Live" bg="rgba(212,175,55,0.14)" color={C.goldDeep}
              border="1px solid rgba(212,175,55,0.30)" />
            <span data-testid="dashboard-ai-widget-mode" style={modeStyle}>
              Mode: {modeLabel}
            </span>
          </div>
          <span style={{ fontFamily: FONT.cormorant, fontWeight: 600, fontSize: 12,
            color: C.textSecondary, display: 'block', marginTop: 2 }}>
            Today's guest concierge activity · click to open Activity & Health
          </span>
        </div>
        <span data-testid="dashboard-ai-widget-cta"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6,
            fontFamily: FONT.system, fontSize: 11, fontWeight: 600,
            color: C.goldDark, letterSpacing: '0.04em' }}>
          View Activity & Health <ArrowRight size={13} />
        </span>
      </div>

      {/* Stats row */}
      {loading ? (
        <div data-testid="dashboard-ai-widget-loading"
          style={{ padding: '14px 0', fontSize: 11, color: C.textMuted, fontStyle: 'italic' }}>
          Loading Oscar telemetry…
        </div>
      ) : error ? (
        <div data-testid="dashboard-ai-widget-error"
          style={{ padding: '10px 12px', borderRadius: 10,
            background: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.20)',
            fontSize: 11, color: C.statusRedDark }}>
          Couldn't load Oscar telemetry — {error}
        </div>
      ) : empty ? (
        <div data-testid="dashboard-ai-widget-empty"
          style={{ padding: '14px 16px', borderRadius: 12,
            background: 'rgba(255,255,255,0.30)',
            border: '1px dashed rgba(212,175,55,0.30)',
            display: 'flex', alignItems: 'center', gap: 10 }}>
          <Activity size={16} color={C.goldPrimary} strokeWidth={1.6} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <span style={{ fontFamily: FONT.system, fontSize: 12, fontWeight: 600,
              color: C.textPrimary, display: 'block' }}>
              No Oscar activity yet
            </span>
            <span style={{ fontFamily: FONT.cormorant, fontWeight: 600,
              fontSize: 11, color: C.textSecondary }}>
              Once guests interact with Oscar, today's snapshot will appear here.
            </span>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            {/* Today's turns */}
            <div data-testid="dashboard-ai-widget-turns"
              style={{ padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.32)',
                border: '1px solid rgba(212,175,55,0.18)' }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                textTransform: 'uppercase', color: C.textMuted, display: 'block',
                marginBottom: 4 }}>
                Today's Turns
              </span>
              <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 26,
                color: C.textPrimary, display: 'block', lineHeight: 1.05 }}>
                {(todayRow.turns || 0).toLocaleString()}
              </span>
              <span style={{ fontFamily: FONT.cormorant, fontWeight: 600,
                fontSize: 11, color: C.textSecondary, display: 'block', marginTop: 2 }}>
                {(todayRow.llm_calls || 0).toLocaleString()} LLM call{todayRow.llm_calls === 1 ? '' : 's'}
                {todayRow.escalated ? ` · ${todayRow.escalated} escalated` : ''}
              </span>
            </div>

            {/* Today's approx cost */}
            <div data-testid="dashboard-ai-widget-cost"
              style={{ padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.32)',
                border: '1px solid rgba(212,175,55,0.18)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                  textTransform: 'uppercase', color: C.textMuted }}>
                  Today's Approx Cost
                </span>
                <span data-testid="dashboard-ai-widget-cost-approx-badge"
                  style={{ fontSize: 8, fontWeight: 800, padding: '1px 5px',
                    borderRadius: 99, background: 'rgba(212,175,55,0.16)',
                    color: C.goldDeep, border: '1px solid rgba(212,175,55,0.32)',
                    letterSpacing: '0.04em' }}>
                  ≈ approx
                </span>
              </div>
              <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 26,
                color: C.textPrimary, display: 'block', lineHeight: 1.05 }}>
                ${(todayRow.cost || 0).toFixed(4)}
              </span>
              <span style={{ fontFamily: FONT.cormorant, fontWeight: 600,
                fontSize: 11, color: C.textSecondary, display: 'block', marginTop: 2,
                fontStyle: 'italic' }}>
                Estimate · not authoritative billing
              </span>
            </div>

            {/* Pending unknowns */}
            <div data-testid="dashboard-ai-widget-pending"
              style={{ padding: '12px 14px', borderRadius: 12,
                background: 'rgba(255,255,255,0.32)',
                border: '1px solid rgba(212,175,55,0.18)' }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                textTransform: 'uppercase', color: C.textMuted, display: 'block',
                marginBottom: 4 }}>
                Pending Unknowns
              </span>
              <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 26,
                color: pendingUnknown > 0 ? C.goldDark : C.textPrimary,
                display: 'block', lineHeight: 1.05 }}>
                {pendingUnknown.toLocaleString()}
              </span>
              <span style={{ fontFamily: FONT.cormorant, fontWeight: 600,
                fontSize: 11, color: C.textSecondary, display: 'block', marginTop: 2,
                fontStyle: pendingUnknown > 0 && topPending ? 'italic' : 'normal',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {pendingUnknown === 0
                  ? 'Queue clear'
                  : (topPending && topPending.question
                      ? `Top: "${topPending.question.length > 64 ? topPending.question.slice(0, 64) + '…' : topPending.question}"`
                      : 'Awaiting promote-to-KB')}
              </span>
            </div>
          </div>

          {/* Escalation rate strip — only renders when at least one turn was
              recorded in the window (escalation_rate is null otherwise). */}
          {typeof escalationRate === 'number' && (
            <div data-testid="dashboard-ai-widget-escalation"
              style={{ display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', borderRadius: 10,
                background: 'rgba(255,255,255,0.22)',
                border: '1px solid rgba(212,175,55,0.18)' }}>
              <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: '0.10em',
                textTransform: 'uppercase', color: C.textMuted }}>
                Escalation Rate
              </span>
              <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 14,
                color: escalationRate >= 0.20 ? C.goldDark : C.textPrimary,
                lineHeight: 1 }}>
                {(escalationRate * 100).toFixed(1)}%
              </span>
              <span style={{ fontFamily: FONT.cormorant, fontWeight: 600,
                fontSize: 11, color: C.textSecondary, fontStyle: 'italic',
                marginLeft: 'auto' }}>
                Share of turns Oscar deferred to the family
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
   ═══════════════════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════════════════
   WORDS FOR CAROL — Dashboard card
   --------------------------------------------------------------------------
   Surfaces guest answers to the RSVP prompt "Describe Carol in 1 word."
   Source: GET /api/dashboard/words-for-carol
     - entries: latest word per guest, sorted newest first
     - repeats: words used by ≥2 guests (count + guests)

   Visual rhythm matches InviteFunnelWidget (white/glass, gold accents).
   Empty state: "Words will appear here as RSVPs arrive."
   ═══════════════════════════════════════════════════════════════════════════ */
function WordsForCarolWidget() {
  const [data, setData] = useState({ entries: [], repeats: [], total_responses: 0 });
  const [loading, setLoading] = useState(true);
  const [openRepeat, setOpenRepeat] = useState(null); // word key for popover
  const [confirmDeleteId, setConfirmDeleteId] = useState(null); // message_id awaiting confirm
  const [deletingId, setDeletingId] = useState(null);
  const [confirmClearAll, setConfirmClearAll] = useState(false); // two-step modal open
  const [clearingAll, setClearingAll] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/dashboard/words-for-carol`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setData(json || { entries: [], repeats: [], total_responses: 0 });
    } catch (_) {
      setData({ entries: [], repeats: [], total_responses: 0 });
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await refresh();
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [refresh]);

  // Close popover on outside click.
  useEffect(() => {
    if (!openRepeat) return undefined;
    const onDocClick = () => setOpenRepeat(null);
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [openRepeat]);

  // Auto-cancel a row's pending confirm if the user moves elsewhere.
  useEffect(() => {
    if (!confirmDeleteId) return undefined;
    const t = setTimeout(() => setConfirmDeleteId(null), 6000);
    return () => clearTimeout(t);
  }, [confirmDeleteId]);

  const deleteOne = async (messageId) => {
    setDeletingId(messageId);
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/dashboard/words-for-carol/${messageId}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setConfirmDeleteId(null);
      await refresh();
    } catch (_) {
      setErrorMsg("Couldn't delete that word. Please try again.");
    } finally {
      setDeletingId(null);
    }
  };

  const clearAll = async () => {
    setClearingAll(true);
    setErrorMsg('');
    try {
      const res = await fetch(`${API}/api/dashboard/words-for-carol`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setConfirmClearAll(false);
      await refresh();
    } catch (_) {
      setErrorMsg("Couldn't clear words. Please try again.");
    } finally {
      setClearingAll(false);
    }
  };

  const entries = data.entries || [];
  const repeats = data.repeats || [];
  const total = Number(data.total_responses || entries.length || 0);
  const isEmpty = !loading && entries.length === 0;

  return (
    <div
      data-testid="words-for-carol-widget"
      style={{ ...glassStd, padding: 20, display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 400, overflow: 'hidden' }}
    >
      <TopShine />
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0, gap: 8 }}>
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
          Words for Carol
        </span>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span
            data-testid="words-for-carol-count"
            style={{ fontFamily: FONT.cormorant, fontSize: 11, color: C.textSecondary, fontStyle: 'italic' }}
          >
            {total} submitted
          </span>
          {total > 0 && (
            <button
              type="button"
              data-testid="words-for-carol-clear-all-btn"
              onClick={(e) => { e.stopPropagation(); setConfirmClearAll(true); }}
              style={{
                padding: '3px 9px', borderRadius: 6, cursor: 'pointer',
                background: 'rgba(220,38,38,0.06)',
                border: '1px solid rgba(220,38,38,0.30)',
                fontFamily: FONT.system, fontSize: 9.5, fontWeight: 700,
                color: C.statusRedDark, letterSpacing: '0.06em',
                textTransform: 'uppercase',
              }}
            >
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* Body — two-column split. Both columns scroll INTERNALLY so the card never grows. */}
      <div
        data-testid="words-for-carol-body"
        style={{
          flex: 1, minHeight: 0, display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.35fr) 1px minmax(0, 1fr)',
          gap: 12,
        }}
      >
        {/* LEFT — main list (Name | Word) */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 9.5,
            textTransform: 'uppercase', letterSpacing: '0.12em',
            color: C.textSecondary, marginBottom: 6, flexShrink: 0,
          }}>
            Responses
          </div>
          <div
            data-testid="words-for-carol-list"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}
          >
            {loading && (
              <div style={{
                fontFamily: FONT.cormorant, fontSize: 12, fontStyle: 'italic',
                color: C.textSecondary, textAlign: 'center', padding: '24px 0',
              }}>
                Loading…
              </div>
            )}

            {isEmpty && (
              <div
                data-testid="words-for-carol-empty"
                style={{
                  fontFamily: FONT.cormorant, fontSize: 13, fontStyle: 'italic',
                  color: C.textSecondary, textAlign: 'center',
                  padding: '28px 8px', lineHeight: 1.5,
                }}
              >
                Words will appear here as RSVPs arrive.
              </div>
            )}

            {!loading && entries.map((e, idx) => {
              const awaitingConfirm = confirmDeleteId === e.message_id;
              const isDeleting = deletingId === e.message_id;
              return (
                <div
                  key={e.message_id || `${e.guest_name}-${idx}`}
                  data-testid={`words-for-carol-row-${idx}`}
                  className="wfc-row"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '7px 2px',
                    borderBottom: idx === entries.length - 1 ? 'none' : '1px solid rgba(0,0,0,0.05)',
                    opacity: isDeleting ? 0.5 : 1,
                    transition: 'opacity 120ms',
                  }}
                >
                  <span
                    data-testid={`words-for-carol-name-${idx}`}
                    style={{
                      flex: '1 1 0', minWidth: 0,
                      fontFamily: FONT.cormorant, fontSize: 13, fontWeight: 600,
                      color: C.textSecondary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {e.guest_name || 'Guest'}
                  </span>
                  <span
                    data-testid={`words-for-carol-word-${idx}`}
                    style={{
                      flexShrink: 0, maxWidth: '50%',
                      fontFamily: FONT.playfair, fontSize: 13.5, fontWeight: 700,
                      color: C.textPrimary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}
                  >
                    {e.word}
                  </span>
                  {/* Delete control — two-step. First click flips to 'Confirm?' state. */}
                  {!awaitingConfirm ? (
                    <button
                      type="button"
                      data-testid={`words-for-carol-delete-${idx}`}
                      title="Delete word"
                      onClick={(ev) => { ev.stopPropagation(); setConfirmDeleteId(e.message_id); }}
                      disabled={isDeleting}
                      className="wfc-row-delete"
                      style={{
                        flexShrink: 0, width: 22, height: 22, padding: 0,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        borderRadius: 6, border: '1px solid rgba(0,0,0,0.06)',
                        background: 'rgba(255,255,255,0.55)',
                        cursor: isDeleting ? 'default' : 'pointer',
                        color: C.textMuted,
                        opacity: 0,
                        transition: 'opacity 120ms, background 120ms, color 120ms',
                      }}
                      onMouseEnter={(ev) => {
                        ev.currentTarget.style.background = 'rgba(220,38,38,0.10)';
                        ev.currentTarget.style.color = C.statusRedDark;
                      }}
                      onMouseLeave={(ev) => {
                        ev.currentTarget.style.background = 'rgba(255,255,255,0.55)';
                        ev.currentTarget.style.color = C.textMuted;
                      }}
                    >
                      <Trash2 size={11} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      data-testid={`words-for-carol-confirm-delete-${idx}`}
                      onClick={(ev) => { ev.stopPropagation(); deleteOne(e.message_id); }}
                      disabled={isDeleting}
                      style={{
                        flexShrink: 0, padding: '3px 8px',
                        borderRadius: 6, border: '1px solid rgba(220,38,38,0.45)',
                        background: 'rgba(220,38,38,0.10)',
                        cursor: isDeleting ? 'default' : 'pointer',
                        color: C.statusRedDark,
                        fontFamily: FONT.system, fontSize: 9.5, fontWeight: 800,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                      }}
                    >
                      {isDeleting ? 'Deleting…' : 'Confirm Delete?'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Vertical gold divider */}
        <div style={{
          width: 1, alignSelf: 'stretch',
          background: 'linear-gradient(180deg, transparent 0%, rgba(212,175,55,0.30) 20%, rgba(212,175,55,0.30) 80%, transparent 100%)',
        }} />

        {/* RIGHT — repeated words mini-table */}
        <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, minWidth: 0 }}>
          <div style={{
            fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 9.5,
            textTransform: 'uppercase', letterSpacing: '0.12em',
            color: C.textSecondary, marginBottom: 6, flexShrink: 0,
          }}>
            Repeated
          </div>
          <div
            data-testid="words-for-carol-repeats"
            style={{ flex: 1, minHeight: 0, overflowY: 'auto', paddingRight: 4 }}
          >
            {repeats.length === 0 && (
              <div style={{
                fontFamily: FONT.cormorant, fontSize: 12, fontStyle: 'italic',
                color: C.textSecondary, padding: '8px 2px', lineHeight: 1.4,
              }}>
                No repeats yet.
              </div>
            )}

            {repeats.map((r) => {
              const wkey = r.word.toLowerCase();
              const isOpen = openRepeat === wkey;
              return (
                <div key={wkey} style={{ position: 'relative' }}>
                  <button
                    type="button"
                    data-testid={`words-for-carol-repeat-${wkey}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenRepeat(isOpen ? null : wkey);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(212,175,55,0.08)';
                      setOpenRepeat(wkey);
                    }}
                    onMouseLeave={(e) => {
                      if (!isOpen) e.currentTarget.style.background = 'transparent';
                    }}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 6px', border: 'none', borderRadius: 6,
                      background: isOpen ? 'rgba(212,175,55,0.10)' : 'transparent',
                      cursor: 'pointer', textAlign: 'left',
                      borderBottom: '1px solid rgba(0,0,0,0.05)',
                      transition: 'background 120ms',
                    }}
                  >
                    <span style={{
                      flex: '1 1 0', minWidth: 0,
                      fontFamily: FONT.playfair, fontWeight: 700, fontSize: 13,
                      color: C.textPrimary,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.word}
                    </span>
                    <span style={{
                      flexShrink: 0,
                      fontFamily: FONT.cormorant, fontSize: 11, fontWeight: 700,
                      color: C.goldDark, letterSpacing: '0.04em',
                      minWidth: 22, textAlign: 'right',
                    }}>
                      ×{r.count}
                    </span>
                  </button>
                  {isOpen && (
                    <div
                      data-testid={`words-for-carol-repeat-pop-${wkey}`}
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        position: 'absolute', top: 'calc(100% - 2px)', right: 0,
                        minWidth: 150, maxWidth: 220, zIndex: 30,
                        background: 'rgba(255,255,255,0.97)',
                        backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)',
                        border: '1px solid rgba(212,175,55,0.30)',
                        borderRadius: 10, boxShadow: SHADOW.tooltip,
                        padding: '8px 10px',
                      }}
                    >
                      <div style={{
                        fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 9.5,
                        textTransform: 'uppercase', letterSpacing: '0.10em',
                        color: C.textSecondary, marginBottom: 4,
                      }}>
                        Said by
                      </div>
                      <div style={{
                        fontFamily: FONT.cormorant, fontSize: 12.5, color: C.textPrimary,
                        lineHeight: 1.45,
                      }}>
                        {r.guests.join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Reveal row delete on hover (without growing height) */}
      <style>{`
        .wfc-row:hover .wfc-row-delete { opacity: 1 !important; }
      `}</style>

      {errorMsg && (
        <div
          data-testid="words-for-carol-error"
          style={{
            marginTop: 8, padding: '6px 10px', borderRadius: 6,
            background: 'rgba(220,38,38,0.06)',
            border: '1px solid rgba(220,38,38,0.25)',
            fontFamily: FONT.cormorant, fontSize: 11.5, color: C.statusRedDark,
            flexShrink: 0,
          }}
        >
          {errorMsg}
        </div>
      )}

      {/* Two-step Clear All confirmation modal — overlays the card area */}
      {confirmClearAll && (
        <div
          data-testid="words-for-carol-clear-all-modal"
          onClick={() => !clearingAll && setConfirmClearAll(false)}
          style={{
            position: 'absolute', inset: 0, zIndex: 40,
            background: 'rgba(255,255,255,0.78)',
            backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
            borderRadius: 16,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 18,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 320,
              background: '#fff',
              border: '1px solid rgba(220,38,38,0.30)',
              borderRadius: 12,
              boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
              padding: '14px 16px',
            }}
          >
            <div style={{
              fontFamily: FONT.playfair, fontWeight: 700, fontSize: 14,
              color: C.textPrimary, marginBottom: 6,
            }}>
              Clear all words?
            </div>
            <div style={{
              fontFamily: FONT.cormorant, fontSize: 12.5, color: C.textSecondary,
              lineHeight: 1.45, marginBottom: 14,
            }}>
              This will permanently remove all {total} submitted word{total === 1 ? '' : 's'} from the dashboard
              and clear the durable field on every guest record. This action cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                type="button"
                data-testid="words-for-carol-clear-all-cancel"
                onClick={() => setConfirmClearAll(false)}
                disabled={clearingAll}
                style={{
                  padding: '6px 12px', borderRadius: 6, cursor: clearingAll ? 'default' : 'pointer',
                  background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.10)',
                  fontFamily: FONT.system, fontSize: 11, fontWeight: 700,
                  color: C.textPrimary, letterSpacing: '0.04em',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="words-for-carol-clear-all-confirm"
                onClick={clearAll}
                disabled={clearingAll}
                style={{
                  padding: '6px 12px', borderRadius: 6, cursor: clearingAll ? 'default' : 'pointer',
                  background: C.statusRed, border: '1px solid ' + C.statusRedDark,
                  fontFamily: FONT.system, fontSize: 11, fontWeight: 800,
                  color: '#fff', letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >
                {clearingAll ? 'Clearing…' : 'Confirm Clear All'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   InviteFunnelWidget — Step 2 token-open tracking (party/invite-level only)
   --------------------------------------------------------------------------
   ONE consolidated card showing the invite-open funnel. Wording rule:
   this is invite-level truth, not certainty about a specific guest.

   Primary buckets (mutually exclusive, sum to total invited parties):
     • Not yet sent           → at least one guest in party still has
                                  sent_invitation === false
     • Sent · not opened      → all guests sent, but invite link never opened
     • Opened · awaiting RSVP → invite opened, RSVP not yet submitted
     • Opened · RSVPed        → invite opened AND RSVP submitted

   Sub-status (subset of "Opened · awaiting RSVP"):
     • Reached landing only   → opened, RSVP not submitted, and the deepest
                                  route reached is still the landing page
                                  (deepest_route_reached === "invite")

   Each segment is clickable → drills into the Guests tab pre-filtered.
   ═══════════════════════════════════════════════════════════════════════════ */
function InviteFunnelWidget({ guestStats, onNavigate }) {
  const f = guestStats?.invite_funnel || {
    total_invited_parties: 0,
    not_sent: 0, sent_not_opened: 0,
    opened_not_rsvped: 0, opened_and_rsvped: 0,
    reached_landing_only: 0,
  };
  const total = Math.max(1, Number(f.total_invited_parties || 0));  // avoid /0
  const pct = (n) => Math.round((Number(n || 0) / total) * 100);

  // ── Open-count trend (read-only, sparkline) ──────────────────────────────
  // Bins parties by the day their invite link first opened, over the last 14
  // days. Source: existing /api/parties payload (`first_opened_at`, with
  // `rsvp_submitted_at` as a fallback proof-of-open for legacy records that
  // pre-date the open-tracking instrumentation). No new endpoint, no new
  // fields. If there's no usable history (<2 non-zero days), we render a
  // restrained neutral state — never fabricated data.
  const [trend, setTrend] = useState({ loading: true, days: [] });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/parties`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const parties = await res.json();
        if (cancelled || !Array.isArray(parties)) return;

        const WINDOW_DAYS = 14;
        // Build day keys (UTC) from oldest → newest, ending today.
        const today = new Date();
        const dayKeys = [];
        const counts = {};
        for (let i = WINDOW_DAYS - 1; i >= 0; i--) {
          const d = new Date(today);
          d.setUTCHours(0, 0, 0, 0);
          d.setUTCDate(d.getUTCDate() - i);
          const k = d.toISOString().slice(0, 10);
          dayKeys.push(k);
          counts[k] = 0;
        }
        const oldestMs = new Date(dayKeys[0] + 'T00:00:00Z').getTime();

        parties.forEach((p) => {
          // Pick the canonical "opened" timestamp for this party. Mirrors the
          // funnel rule: rsvp_submitted_at is proof-of-open even when the
          // first_opened_at field wasn't captured (legacy data).
          const ts = p?.first_opened_at || p?.rsvp_submitted_at || null;
          if (!ts) return;
          const t = new Date(ts).getTime();
          if (Number.isNaN(t) || t < oldestMs) return;
          const k = new Date(t).toISOString().slice(0, 10);
          if (k in counts) counts[k] += 1;
        });

        const series = dayKeys.map((k) => ({ day: k, count: counts[k] }));
        if (!cancelled) setTrend({ loading: false, days: series });
      } catch (_) {
        if (!cancelled) setTrend({ loading: false, days: [] });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Sparkline geometry — small, header-scale.
  const SPARK_W = 88;
  const SPARK_H = 22;
  const sparkline = useMemo(() => {
    const pts = trend.days || [];
    const totalOpens = pts.reduce((s, x) => s + (x.count || 0), 0);
    const nonZeroDays = pts.filter((x) => x.count > 0).length;
    const maxC = pts.reduce((m, x) => Math.max(m, x.count || 0), 0);
    // Need at least 2 days with activity for a meaningful trend line.
    const meaningful = nonZeroDays >= 2 && maxC > 0;
    if (!meaningful) {
      return { meaningful, totalOpens, polyline: '', area: '' };
    }
    const stepX = SPARK_W / Math.max(1, pts.length - 1);
    const baseY = SPARK_H - 2;
    const topY = 2;
    const yFor = (c) => baseY - ((c / maxC) * (baseY - topY));
    const coords = pts.map((p, i) => `${(i * stepX).toFixed(2)},${yFor(p.count).toFixed(2)}`);
    const polyline = coords.join(' ');
    const area = `0,${baseY} ${polyline} ${SPARK_W},${baseY}`;
    return { meaningful, totalOpens, polyline, area };
  }, [trend]);

  // Segment definitions, ordered top-to-bottom matching funnel progression.
  const segs = [
    { key: 'not_sent',          label: 'Not yet sent',           count: f.not_sent,          bar: '#9CA3AF', filter: 'invite_not_sent' },
    { key: 'sent_not_opened',   label: 'Sent · not opened',      count: f.sent_not_opened,   bar: '#D97706', filter: 'invite_sent_not_opened' },
    { key: 'opened_not_rsvped', label: 'Opened · awaiting RSVP', count: f.opened_not_rsvped, bar: '#D4AF37', filter: 'invite_opened_not_rsvped' },
    { key: 'opened_and_rsvped', label: 'Opened · RSVPed',        count: f.opened_and_rsvped, bar: '#16A34A', filter: 'invite_opened_and_rsvped' },
  ];

  const handleNav = (filter) => {
    // One-shot sessionStorage handoff (same pattern as Milo prefills).
    // GuestListTab reads this on mount, applies the filter, removes the key.
    try {
      sessionStorage.setItem('admin_guest_list_invite_filter', String(filter || ''));
    } catch (_) {}
    if (typeof onNavigate === 'function') {
      onNavigate('guests');
    }
  };

  // Resolve the sparkline element pre-return so the JSX stays flat and the
  // visual-editor instrumentation can statically reason about it.
  let sparklineEl = null;
  if (!trend.loading) {
    if (sparkline.meaningful) {
      sparklineEl = (
        <div
          data-testid="invite-funnel-sparkline"
          title={`${sparkline.totalOpens} open${sparkline.totalOpens === 1 ? '' : 's'} in the last 14 days`}
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <svg
            width={SPARK_W}
            height={SPARK_H}
            viewBox={`0 0 ${SPARK_W} ${SPARK_H}`}
            style={{ display: 'block', overflow: 'visible' }}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="wfc-spark-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"  stopColor="#D4AF37" stopOpacity="0.30" />
                <stop offset="100%" stopColor="#D4AF37" stopOpacity="0.00" />
              </linearGradient>
            </defs>
            <polygon points={sparkline.area} fill="url(#wfc-spark-fill)" />
            <polyline
              points={sparkline.polyline}
              fill="none"
              stroke="#B8860B"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span style={{
            fontFamily: FONT.cormorant, fontSize: 10, fontWeight: 700,
            color: C.goldDark, letterSpacing: '0.05em', textTransform: 'uppercase',
          }}>
            14d
          </span>
        </div>
      );
    } else {
      sparklineEl = (
        <div
          data-testid="invite-funnel-sparkline-empty"
          title="Not enough open history yet — trend will appear after multiple days of activity."
          style={{ display: 'flex', alignItems: 'center', gap: 6 }}
        >
          <svg width={SPARK_W} height={SPARK_H} viewBox={`0 0 ${SPARK_W} ${SPARK_H}`} aria-hidden="true">
            <line
              x1="0" y1={SPARK_H - 2} x2={SPARK_W} y2={SPARK_H - 2}
              stroke="rgba(0,0,0,0.18)" strokeWidth="1" strokeDasharray="2 3"
            />
          </svg>
          <span style={{
            fontFamily: FONT.cormorant, fontSize: 10, fontStyle: 'italic',
            color: C.textMuted, letterSpacing: '0.04em',
          }}>
            No trend yet
          </span>
        </div>
      );
    }
  }

  // Compact integrated card — same shape/footprint as Priority Alerts.
  // No sub-tile KPI grid: bar + 4 compact rows + sub-status + caveat.
  return (
    <div
      data-testid="invite-funnel-widget"
      style={{ ...glassStd, padding: 20, display: 'flex', flexDirection: 'column', height: '100%', maxHeight: 400, overflow: 'hidden' }}>
      <TopShine />
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0, gap: 10 }}>
        <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 16, color: C.textPrimary }}>
          Invite Open Funnel
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {sparklineEl}
          <span style={{ fontFamily: FONT.cormorant, fontSize: 11, color: C.textSecondary, fontStyle: 'italic' }}>
            {f.total_invited_parties || 0} parties
          </span>
        </div>
      </div>

      {/* Stacked horizontal bar */}
      <div
        data-testid="invite-funnel-bar"
        style={{
          display: 'flex', height: 10, borderRadius: 6, overflow: 'hidden',
          marginBottom: 12, flexShrink: 0,
          boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.18)',
          background: 'rgba(0,0,0,0.05)',
        }}>
        {segs.map(s => {
          const w = (Number(s.count || 0) / total) * 100;
          if (w <= 0) return null;
          return (
            <div
              key={s.key}
              data-testid={`invite-funnel-bar-${s.key}`}
              title={`${s.label}: ${s.count} (${pct(s.count)}%)`}
              onClick={() => handleNav(s.filter)}
              style={{ width: `${w}%`, background: s.bar, cursor: 'pointer', transition: 'filter 120ms' }}
              onMouseEnter={e => e.currentTarget.style.filter = 'brightness(1.1)'}
              onMouseLeave={e => e.currentTarget.style.filter = 'none'}
            />
          );
        })}
      </div>

      {/* Compact 4-row legend — single integrated list, not separate KPI tiles */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 5 }}>
        {segs.map(s => (
          <div
            key={s.key}
            data-testid={`invite-funnel-seg-${s.key}`}
            onClick={() => handleNav(s.filter)}
            style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '7px 10px', borderRadius: 8, cursor: 'pointer',
              background: 'rgba(255,255,255,0.35)',
              border: '1px solid rgba(0,0,0,0.05)',
              transition: 'background 120ms',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.6)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.35)'}
          >
            <span style={{
              width: 9, height: 9, borderRadius: '50%', background: s.bar, flexShrink: 0,
              boxShadow: '0 0 0 1px rgba(0,0,0,0.10)',
            }} />
            <span style={{ flex: 1, fontFamily: FONT.cormorant, fontSize: 12.5, color: C.textPrimary, fontWeight: 600 }}>
              {s.label}
            </span>
            <span style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 14, color: C.textPrimary }}>
              {Number(s.count || 0)}
            </span>
            <span style={{ fontFamily: FONT.cormorant, fontSize: 11, color: C.textSecondary, width: 32, textAlign: 'right' }}>
              {pct(s.count)}%
            </span>
          </div>
        ))}

        {/* Sub-status — subordinate, inline, not a separate KPI block */}
        {Number(f.opened_not_rsvped || 0) > 0 && (
          <div
            data-testid="invite-funnel-substatus"
            onClick={() => handleNav('invite_reached_landing_only')}
            style={{
              marginTop: 2, padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
              background: 'rgba(212,175,55,0.08)',
              border: '1px dashed rgba(212,175,55,0.40)',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
            <span style={{ flex: 1, fontFamily: FONT.cormorant, fontSize: 11, color: C.textSecondary, fontStyle: 'italic' }}>
              <strong data-testid="invite-funnel-stalled-count" style={{ fontStyle: 'normal', color: C.textPrimary }}>
                {Number(f.reached_landing_only || 0)}
              </strong>{' '}reached landing page but didn't proceed
            </span>
            <ChevronRight size={12} color={C.goldDark} />
          </div>
        )}
      </div>

      {/* Caveat — wording rule reminder */}
      <div style={{
        marginTop: 8, fontFamily: FONT.cormorant, fontSize: 10,
        fontStyle: 'italic', color: C.textSecondary, opacity: 0.75, flexShrink: 0,
      }}>
        Invite-link signal — token is shared per party, so this counts party-level opens, not individual guests.
      </div>
    </div>
  );
}

export default function DashboardTab({
  guestStats = { total: 0, pending: 0, sent: 0, failed: 0, rsvp_yes: 0, rsvp_no: 0 },
  messages = [],
  onNavigate = () => {},
  onDeleteMessage,
  eventConfig = { id: '1', name: '80 Years of Connection', date: 'Jul 12, 2026', location: 'Passaic, NJ', honoree: 'Our Beloved' },
  budget = null,
  vendors = [],
  liquidity = null,
}) {
  // Load Playfair Display font — component-scoped, removed on unmount
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;800&display=swap';
    document.head.appendChild(link);
    return () => { document.head.removeChild(link); };
  }, []);

  const unreadCount = messages.filter(m => m.status === 'unread').length;

  return (
    <div data-testid="dashboard-tab" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20, maxWidth: 1800, margin: '0 auto', position: 'relative' }}>
      {/* Ambient overlay */}
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, background: 'radial-gradient(ellipse at 20% 15%, rgba(212,175,55,0.06) 0%, transparent 50%), radial-gradient(ellipse at 80% 85%, rgba(212,175,55,0.04) 0%, transparent 50%), radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.01) 0%, transparent 70%)' }} />

      {/* Pulse animation style */}
      <style>{`@keyframes dashPulse { 0%, 100% { transform: scale(1); opacity: 0.6; } 50% { transform: scale(1.15); opacity: 0; } }`}</style>

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <span style={{ fontFamily: FONT.cormorant, fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.25em', color: C.textSecondary, display: 'block', marginBottom: 4 }}>Event Command Center</span>
        <h1 style={{ fontFamily: FONT.playfair, fontWeight: 700, fontSize: 34, color: C.textPrimary, margin: 0, lineHeight: 1.1, letterSpacing: '-0.01em' }}>Dashboard</h1>
        <p style={{ fontFamily: FONT.cormorant, fontWeight: 600, fontSize: 14, color: C.textSecondary, margin: '4px 0 0' }}>Real-time overview of your event metrics and operations</p>
        <div style={{ height: 2, background: GRAD.goldDivider, marginTop: 16, boxShadow: '0 1px 4px rgba(212,175,55,0.2)' }} />
      </div>

      {/* KPI INTEGRITY INDICATOR — always visible. Compact pill when passing, expanded alert when failing.
          See /app/memory/KPI_INTEGRITY.md and /app/frontend/src/components/admin/KPIIntegrityIndicator.jsx */}
      <KPIIntegrityIndicator
        integrityCheck={guestStats.integrity_check}
        checkedAt={guestStats.integrity_checked_at}
        variant="header"
        context="dashboard"
        onNavigate={onNavigate}
      />

      {/* Row 1 */}
      <CommandBar guestStats={guestStats} unreadCount={unreadCount} eventConfig={eventConfig} budget={budget} vendors={vendors} onNavigate={onNavigate} />
      {/* Row 2 */}
      <AttendanceRow guestStats={guestStats} minimumGuestTarget={eventConfig.minimum_guest_target} onNavigate={onNavigate} />
      {/* Row 3 — Invite Funnel (left, same shape as Alerts) + Priority Alerts (right). Empty cell on far left = intentional gap. */}
      <AlertsFunnelRow guestStats={guestStats} unreadCount={unreadCount} onNavigate={onNavigate} />
      {/* Row 3b — Event Readiness Timeline moved DOWN to where the funnel previously sat, full-width */}
      <TimelineRow />
      {/* Row 4 */}
      <FinancialRow vendors={vendors} guestStats={guestStats} budget={budget} liquidity={liquidity} onNavigate={onNavigate} />
      {/* Row 5 */}
      <InboxVelocityRow messages={messages} vendors={vendors} guestStats={guestStats} onNavigate={onNavigate} onDeleteMessage={onDeleteMessage} />
      {/* Row 5b — Phase 3D: compact Oscar AI rollup, deep-links to AI → Activity & Health */}
      <OscarAIWidget onNavigate={onNavigate} />
      {/* Row 6 */}
      <ActionsCalendarRow onNavigate={onNavigate} />
    </div>
  );
}
